// Opportunity detail page — SGNL-84.
//
// New SPA route /#opp/{opp_id}. Single page that pulls everything we know
// about one opp into a structured "research surface": LLM summary, tag
// verdicts, the full conversation timeline (interleaved tasks / chatter /
// events / notes / emails), plus four placeholder panels for upcoming
// research POCs (similar opps, customer-success patterns, external
// signal, suggested next move).
//
// Strategic intent: this page is the future landing surface where each
// new POC drops in as one more right-column panel without touching the
// surrounding layout. Today's deliverable is the timeline + the
// architecture; the four placeholders make the roadmap visible inline.
//
// Pure-vanilla DOM, no framework, no per-tab CSS. Every visual element
// reuses existing classes from styles.css (.tab-content, .kpi-strip,
// .kpi, .kpi-label, .kpi-value, .data-table, .chip.*, .btn-secondary,
// .muted, .empty-bucket). Adding a new panel is one function + one
// render call — no shared layout to negotiate.

import {
  fetchConversationRollup,
  fetchDocumentEvidence,
  fetchLostCoaching,
  fetchLostPatternMatches,
  fetchNextAction,
  fetchOpportunityDetail,
  fetchPocDraftMessage,
  fetchRecommendedPocs,
  fetchRecommendedPocsRefresh,
  fetchSimilarOpps,
  fetchThreads,
} from "../api";
import type {
  ConversationRollup,
  DocumentEvidenceResult,
  NextActionOut,
  NextActionReference,
  ThreadsOut,
} from "../types";
import { drawSentimentChart } from "./charts";
import type { SentimentPoint } from "./charts";
import { esc, fmtMoney } from "../lib/format";
import {
  computeAbruptLoss,
  computeStagePeakRarity,
  computeStageTimeline,
  computeStalledStage,
  computeTimeToLoss,
  type StageTimelineEntry,
} from "../lib/lost_intel";
import { renderSafeMarkdown } from "../lib/safe_md";
import { chipClassForConfidence } from "../lib/maturity";
import { getState } from "../state";
import type {
  ConversationEntry,
  OpportunityDetailFull,
  OpportunityTagOut,
  PocPick,
  PocRecommendationsOut,
  SimilarOppRow,
  SimilarOppsResponse,
} from "../types";

function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return `${n.toFixed(0)}%`;
}

// ---- SGNL-212: collapsible right-column cards ----------------------------
//
// Every right-column card on the Deal Detail page is wrapped in the
// SGNL-203 <details> pattern so operators can hide cards they're not
// using. Defaults below — kept centralised so the spec lives in one
// readable map instead of being threaded through every render function.
// "Interpret + act" surfaces (rollup / sentiment / next-move) are open;
// "research / supporting" surfaces (timeline / similar / pocs / docs)
// are collapsed.

// `as const` keeps every key as a literal so `keyof typeof` derives a
// finite 7-member union. Object.freeze stops a future module from
// mutating the defaults at runtime. Both improvements came from the
// SGNL-212 pre-PR review (architect LOW + typescript-pro).
const RIGHT_CARD_OPEN = Object.freeze({
  "conversation-rollup": true,
  "timeline": false,
  "sentiment": true,
  "next-move": true,
  "similar-deals": false,
  "recommended-pocs": false,
  "document-evidence": false,
  // SGNL-218 — Lost-deal analysis card. Renders ONLY when the opp closed
  // lost (see renderLostDealCard); when present, it's the most relevant
  // context on the page, so default open.
  "lost-deal-analysis": true,
} as const);

/** The finite set of valid card slugs. Any new panel must be added to
 *  RIGHT_CARD_OPEN above before it can appear in CollapsibleCardOpts. */
type CardSlug = keyof typeof RIGHT_CARD_OPEN;

interface CollapsibleCardOpts {
  /** Card slug — must be one of the keys in RIGHT_CARD_OPEN. Drives default
   *  open/collapsed state. The literal union prevents typos at every call
   *  site and makes autocomplete available. */
  card: CardSlug;
  /** data-testid on the outer .tab-content wrapper. Each render state
   *  (loading / empty / error / full) keeps its own testid suffix so
   *  the existing tests continue to find the right state. The helper
   *  passes the value through esc() at the attribute boundary so a
   *  future caller that passes runtime data can't inject HTML
   *  (SGNL-212 security review MED). */
  testid: string;
  /** Inner HTML of the <h2> in the summary — can include row-count
   *  spans, "(N)" counts, etc. NOT escaped — caller is responsible
   *  (callers either use compile-time literals or esc() their inputs). */
  headerHTML: string;
  /** Inner HTML of the body — table / list / chart / error message.
   *  NOT escaped — caller is responsible. Refresh / Retry buttons
   *  belong in `summaryActionHTML` so they remain visible when the
   *  card is collapsed (SGNL-212 review HIGH finding — a collapsed
   *  Recommended POCs card in error state had no recovery affordance). */
  bodyHTML: string;
  /** Optional action affordance rendered to the right of the <h2>
   *  inside the <summary>. Wrapper applies `event.stopPropagation()`
   *  so clicking the button doesn't also toggle the <details>. */
  summaryActionHTML?: string;
  /** Optional extra class appended to the outer .tab-content wrapper.
   *  Used by the Next Move card so its existing `.next-move-card`
   *  scoped CSS (e.g. `.next-move-card .next-move-chips`) continues
   *  to match without rewriting styles.css. */
  extraClass?: string;
}

function renderCollapsibleRightCard(opts: CollapsibleCardOpts): string {
  const open = RIGHT_CARD_OPEN[opts.card];
  const cls = opts.extraClass ? ` ${opts.extraClass}` : "";
  // The `<span class="opp-card-summary-action">` wraps any action
  // affordance and uses CSS `pointer-events` + a JS click handler
  // (bindCardSummaryActions below) to stop the click from toggling
  // the <details>. Pre-SGNL-212 the Refresh button lived in the
  // tab-header (always visible); a body-only placement was the
  // regression the review caught.
  const actionHTML = opts.summaryActionHTML
    ? `<span class="opp-card-summary-action" data-stop-summary-toggle>${opts.summaryActionHTML}</span>`
    : "";
  return `
    <div class="tab-content${cls}" data-testid="${esc(opts.testid)}">
      <details class="opp-card-collapsible"${open ? " open" : ""} data-card="${esc(opts.card)}">
        <summary class="opp-card-collapsible-summary"><h2>${opts.headerHTML}</h2>${actionHTML}</summary>
        <div class="opp-card-collapsible-body">
          ${opts.bodyHTML}
        </div>
      </details>
    </div>`;
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return esc(s);
  const iso = d.toISOString();
  return iso.slice(0, 10);
}

function fmtDateTime(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return esc(s);
  const iso = d.toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;
}

// ---- Header ----------------------------------------------------------------

function renderHeader(d: OpportunityDetailFull): string {
  // SGNL-100 — Back link moved out of the tab-header (where it sat
  // top-right next to the opp title) and into a standalone breadcrumb row
  // above the title. Top-left is the conventional spot for navigation
  // and stops it competing visually with the opp name.
  //
  // Two .kpi-strip rows — one for accountability/$ headline, one for the
  // SGNL-78 forecast columns + Owner. Keeps the screen scannable on
  // standard laptop widths.
  return `
    <div class="tab-content" data-testid="opp-header">
      <p style="margin:0 0 8px;">
        <a class="back-link muted" href="#" data-action="back-to-list" data-testid="opp-back-link">← Back to list</a>
      </p>
      <div class="tab-header">
        <h2>${esc(d.name)} <span class="row-count">${esc(d.account_name ?? "—")}</span></h2>
      </div>
      <div class="kpi-strip">
        <div class="kpi"><span class="kpi-label">Stage</span><span class="kpi-value">${esc(d.stage ?? "—")}</span></div>
        <div class="kpi"><span class="kpi-label">Close</span><span class="kpi-value">${esc(fmtDate(d.close_date))}</span></div>
        <div class="kpi"><span class="kpi-label">Amount</span><span class="kpi-value">${fmtMoney(d.amount)}</span></div>
        <div class="kpi"><span class="kpi-label">Prob%</span><span class="kpi-value">${fmtPct(d.probability)}</span></div>
        <div class="kpi"><span class="kpi-label">Owner</span><span class="kpi-value">${esc(d.owner_name ?? "—")}</span></div>
        <div class="kpi"><span class="kpi-label">BU</span><span class="kpi-value">${esc(d.bu ?? "—")}</span></div>
      </div>
      <div class="kpi-strip">
        <div class="kpi"><span class="kpi-label">Portfolio Lead</span><span class="kpi-value">${esc(d.vp ?? "—")}</span></div>
        <div class="kpi"><span class="kpi-label">CY Rev</span><span class="kpi-value">${fmtMoney(d.cy_amount)}</span></div>
        <div class="kpi"><span class="kpi-label">F CY Rev</span><span class="kpi-value">${fmtMoney(d.f_cy_amount)}</span></div>
        <div class="kpi"><span class="kpi-label">Prev Prob</span><span class="kpi-value">${fmtPct(d.previous_probability)}</span></div>
        <div class="kpi"><span class="kpi-label">Prev Amount</span><span class="kpi-value">${fmtMoney(d.previous_amount)}</span></div>
        <div class="kpi"><span class="kpi-label">Industry</span><span class="kpi-value">${esc(d.industry ?? "—")}</span></div>
      </div>
    </div>`;
}

// ---- Left rail -------------------------------------------------------------

function renderSummaryCard(d: OpportunityDetailFull): string {
  // SGNL-100 — Surface "Classified <date>"; tuck model + prompt version
  // (dev-debug provenance) behind a collapsible. <details> is flow
  // content so the wrapper must be a <div>, not a <p> (browsers
  // auto-close <p> at the <details> tag, breaking layout + a11y).
  // SGNL-194: model + prompt version were leaking through the "· details"
  // collapsible. Operators don't need them at the AE Opp Detail surface
  // (admin AI-audit view still surfaces them). Replaced with a simple
  // "AI-generated" marker that conveys provenance without exposing the
  // underlying model name.
  const provenance = d.classified_at
    ? `<div class="muted" style="margin-top:8px; font-size:12px;">
         ✦ AI-generated · ${esc(fmtDateTime(d.classified_at))}
       </div>`
    : `<p class="muted" style="margin-top:8px;">Never classified — use the Refetch & Reclassify action to generate a summary.</p>`;
  const body = d.summary
    ? `<p>${esc(d.summary)}</p>`
    : `<p class="empty-bucket">No summary yet — pending classifier run.</p>`;
  return `
    <div class="tab-content" data-testid="opp-summary">
      <div class="tab-header"><h2>Summary</h2></div>
      ${body}
      ${provenance}
    </div>`;
}

// Maps a tag verdict to the chip palette already defined in styles.css —
// keeps the look consistent with other tabs (no new CSS).
function chipClassForVerdict(verdict: string): string {
  if (verdict === "yes") return "chip aqua";
  if (verdict === "no") return "chip coral";
  if (verdict === "maybe") return "chip indigo";
  return "chip blue";
}

// SGNL-29 — Maturity suggestion card on the opp detail page. Stage badge
// + confidence, expandable rationale, and the locked Apply button. Lives
// just below the summary card in the left rail. Rep-set ai_maturity (if
// present) shows above the badge as the canonical value — the suggestion
// stays advisory until R6 SF write-back lands (Epic SGNL-6, Low priority).
function renderMaturityCard(d: OpportunityDetailFull): string {
  const s = d.maturity_suggestion;
  if (!s && !d.ai_maturity) {
    return `
      <div class="tab-content" data-testid="opp-maturity">
        <details class="opp-card-collapsible">
          <summary class="opp-card-collapsible-summary"><h2>AI Maturity</h2></summary>
          <div class="opp-card-collapsible-body">
            <p class="empty-bucket">No rep-set maturity and no suggestion yet.</p>
          </div>
        </details>
      </div>`;
  }
  const repBlock = d.ai_maturity
    ? `<p><strong>Rep-set:</strong> ${esc(d.ai_maturity)}</p>`
    : `<p class="muted">No rep-set maturity yet.</p>`;
  let suggestionBlock = "";
  if (s) {
    // SGNL-107 + AR #2 + PR-Agent T2 on PR #54: coerce ONCE and pass
    // the numeric through to all consumers (chipClassForConfidence,
    // formatters). Omit the data attribute entirely on non-finite
    // values so E2E selectors fail-open instead of matching a sentinel.
    const stageNum = Number(s.suggested_stage);
    const confNum = Number(s.confidence);
    const stageOk = Number.isFinite(stageNum);
    const confOk = Number.isFinite(confNum);
    const stageAttr = stageOk ? ` data-stage="${esc(String(stageNum))}"` : "";
    const confAttr = confOk ? ` data-conf="${esc(confNum.toFixed(2))}"` : "";
    const stageText = stageOk ? String(stageNum) : "?";
    const conf = confOk ? (confNum * 100).toFixed(0) : "?";
    // SGNL-96 — stale verdict (older prompt_version) renders muted with
    // a "stale" qualifier so operators can tell at-a-glance which rows
    // still need re-classification during the v1→v2 rerun window.
    // chipClassForConfidence takes the coerced numeric — pre-fix it
    // received the raw possibly-string `s.confidence` and could crash.
    const chipCls = s.is_stale || !confOk ? "chip" : chipClassForConfidence(confNum);
    const staleNote = s.is_stale
      ? ` <span class="muted" style="font-size:11px;">(stale — older rubric, will re-suggest)</span>`
      : "";
    // SGNL-194 — model + prompt version no longer rendered at user-
    // facing surfaces (admin AI-audit view still surfaces them). Keep
    // the timestamp because it's actionable for the AE (tells them how
    // recent the suggestion is).
    const generatedAt = esc(fmtDateTime(s.created_at));
    suggestionBlock = `
      <div style="margin-top:10px;">
        <span class="${chipCls}" data-testid="opp-maturity-badge"${stageAttr}${confAttr}
              data-stale="${s.is_stale ? "true" : "false"}">
          suggested: Stage ${stageText} (${conf}%)
        </span>${staleNote}
        <button type="button" class="btn-secondary" disabled
                data-testid="opp-maturity-apply"
                title="Available in R6 (Salesforce write-back)"
                style="margin-left:6px; opacity:0.6; pointer-events:none;">
          Apply
        </button>
      </div>
      ${
        s.rationale
          ? `<p class="muted" style="margin:6px 0 0; font-size:12px;">${esc(s.rationale)}</p>`
          : ""
      }
      <div class="muted" style="margin:4px 0 0; font-size:11px;">
        ✦ AI-generated · ${generatedAt}
      </div>`;
  }
  return `
    <div class="tab-content" data-testid="opp-maturity">
      <details class="opp-card-collapsible" open>
        <summary class="opp-card-collapsible-summary"><h2>AI Maturity</h2></summary>
        <div class="opp-card-collapsible-body">
          ${repBlock}
          ${suggestionBlock}
        </div>
      </details>
    </div>`;
}

/** Render the structured-citations block (or empty-state) for one tag.
 * SGNL-128 follow-up: the Opp Detail page's inline `<details>`
 * tag-chip expansion is a different surface than the floating popover
 * in tag_popover.ts; this helper renders the same citation list inline
 * below the rationale so the feature works on BOTH surfaces. */
function renderTagCitationsInline(t: OpportunityTagOut, oppId: string): string {
  const citations = t.citations ?? [];
  if (citations.length === 0) {
    // Older classifications (pre-v1.2) carry no citations for yes/maybe
    // verdicts; surface the empty state so the operator knows why
    // there's nothing to click. No note on 'no' verdicts — those
    // don't carry citations by design.
    if (t.verdict === "yes" || t.verdict === "maybe") {
      return `<p class="muted" style="margin:6px 0 0 4px; font-size:11px; font-style:italic;">
        Older classification — no structured citations.
      </p>`;
    }
    return "";
  }
  // code-reviewer F-02 + architect ARCH-H1 on PR #70: removed the
  // identity-map _SOURCE_TYPE_LABEL — every key mapped to itself, so
  // the lookup was a no-op vs interpolating source_type directly. The
  // map was also duplicated in tag_popover.ts which made adding a new
  // backend source_type a multi-edit hazard.
  const items = citations
    .map((c) => {
      const snippet = (c.snippet ?? "").trim();
      const snippetMarkup = snippet
        ? ` <span class="cite-snippet">"${esc(snippet)}"</span>`
        : "";
      return `<li>
        <a class="cite-link"
           href="#opp/${esc(oppId)}?cite=${esc(c.source_type)}:${esc(c.source_id)}"
           data-cite-source-type="${esc(c.source_type)}"
           data-cite-source-id="${esc(c.source_id)}">
          [${esc(c.source_type)}]${snippetMarkup}
        </a>
      </li>`;
    })
    .join("");
  return `
    <div class="cite-block" style="margin:6px 0 0 4px;">
      <div class="muted" style="font-size:11px; margin-bottom:4px;">
        Cited source rows (click to jump):
      </div>
      <ul class="cite-list" data-testid="opp-tag-citations">${items}</ul>
    </div>`;
}

function renderTagsCard(d: OpportunityDetailFull): string {
  if (d.tags.length === 0) {
    return `
      <div class="tab-content" data-testid="opp-tags">
        <details class="opp-card-collapsible">
          <summary class="opp-card-collapsible-summary"><h2>Tag verdicts</h2></summary>
          <div class="opp-card-collapsible-body">
            <p class="empty-bucket">No tag verdicts yet.</p>
          </div>
        </details>
      </div>`;
  }
  return `
    <div class="tab-content" data-testid="opp-tags">
      <details class="opp-card-collapsible">
        <summary class="opp-card-collapsible-summary">
          <h2>Tag verdicts <span class="muted" style="font-weight:400; font-size:12px;">(${d.tags.length})</span></h2>
        </summary>
        <div class="opp-card-collapsible-body">
          <div class="tag-chip-grid" style="display:flex; flex-wrap:wrap; gap:6px;">
            ${d.tags
              .map(
                (t) => `
              <details class="tag-chip-row" style="flex:0 0 100%;">
                <summary style="cursor:pointer; list-style:none;">
                  <span class="${chipClassForVerdict(t.verdict)}" data-testid="opp-tag-chip">
                    ${esc(t.label)} · ${esc(t.verdict)} · ${(t.confidence * 100).toFixed(0)}%
                  </span>
                </summary>
                <p class="muted" style="margin:6px 0 0 4px; font-size:12px;">
                  ${esc(t.rationale ?? "(no rationale recorded)")}
                </p>
                ${renderTagCitationsInline(t, d.id)}
              </details>`,
              )
              .join("")}
          </div>
        </div>
      </details>
    </div>`;
}

function renderAdminCard(d: OpportunityDetailFull): string {
  // Visible to every signed-in user — the Refetch button hits the
  // admin-gated endpoint, which 403s for non-admins; the inline error
  // path surfaces that cleanly. Same shape as the SGNL-83 admin grid.
  // SGNL-203 / code-reviewer F-01+F-02: keep `.tab-content` as the
  // outer wrapper so card padding behaves consistently; put the
  // collapsible <details> INSIDE so the Refetch button isn't a direct
  // child of <details> (avoids cross-browser click-toggle ambiguity).
  return `
    <div class="tab-content" data-testid="opp-admin">
      <details class="opp-card-collapsible">
        <summary class="opp-card-collapsible-summary"><h2>Admin</h2></summary>
        <div class="opp-card-collapsible-body">
          <p>
            <button type="button" class="btn-secondary"
                    data-action="refetch" data-opp-id="${esc(d.id)}"
                    data-testid="opp-refetch-btn">
              Refetch &amp; Reclassify
            </button>
          </p>
          <p class="muted" style="font-size:12px;">
            <a href="/api/admin/llm-calls?opportunity_id=${encodeURIComponent(d.id)}"
               target="_blank" rel="noopener">View LLM call audit log →</a>
          </p>
          <p class="muted reclassify-feedback" data-testid="opp-refetch-feedback"></p>
        </div>
      </details>
    </div>`;
}

// ---- Right column — timeline + POC placeholders ----------------------------

function timelineKey(e: ConversationEntry): string {
  // Activity_date trumps sf_last_modified when present (Tasks have it,
  // others don't) so a Task with an older sf_last_modified than its
  // last metadata sync still slots correctly by when the conversation
  // actually happened.
  return e.activity_date ?? e.sf_last_modified;
}

function sourceIcon(source: ConversationEntry["source"]): string {
  if (source === "task") return "📧"; // email-channel-as-Task convention
  if (source === "chatter") return "💬";
  if (source === "event") return "📅";
  if (source === "note") return "📝";
  if (source === "email") return "✉️";
  return "•";
}

function renderTimelineEntry(e: ConversationEntry): string {
  const directionLabel = e.direction
    ? ` <span class="muted" style="font-size:11px;">(${esc(e.direction)})</span>`
    : "";
  const subject = e.subject || `(no subject — ${e.source})`;
  const summary = e.summary ?? "(no summary yet)";
  const body = e.anonymized_body
    ? `<details style="margin-top:6px;">
         <summary class="muted" style="cursor:pointer; font-size:12px;">Show full anonymized body</summary>
         <pre style="white-space:pre-wrap; font-size:12px; margin:6px 0 0; padding:8px; background:var(--bg); border:1px solid var(--line); border-radius:4px;">${esc(e.anonymized_body)}</pre>
       </details>`
    : "";
  // SGNL-128 — `data-source-type` + `data-source-id` are how a citation
  // click-through finds this row. Match the source_type names the
  // backend emits exactly (task/chatter/event/note/email/attachment).
  return `
    <li class="timeline-entry"
        data-source="${esc(e.source)}"
        data-source-type="${esc(e.source)}"
        data-source-id="${esc(e.id)}"
        data-testid="opp-timeline-entry">
      <div style="display:flex; align-items:baseline; gap:8px;">
        <span aria-hidden="true">${sourceIcon(e.source)}</span>
        <strong>${esc(subject)}</strong>${directionLabel}
        <span class="muted" style="font-size:11px; margin-left:auto;">
          ${esc(fmtDate(timelineKey(e)))}
        </span>
      </div>
      <p style="margin:4px 0 0 24px;">${esc(summary)}</p>
      <div style="margin:0 0 0 24px;">${body}</div>
    </li>`;
}

function renderTimelinePanel(d: OpportunityDetailFull): string {
  const all: ConversationEntry[] = [
    ...d.tasks,
    ...d.chatter,
    ...d.events,
    ...d.notes,
    ...d.emails,
  ];
  // Order DESC by timeline key — newest first matches operator intent on
  // the existing tabs (Last classified asc on admin grid is a special
  // case there because rows-needing-action surface first; for narrative
  // history, newest-first is the standard).
  // Tiebreakers (sf_last_modified DESC, then id ASC) make the order
  // deterministic when two rows share the same key — important for
  // visual stability across re-renders. PR-Agent finding (importance 6)
  // on PR #37: the previous one-line comparator returned 1 for equals,
  // so even on a stable engine the equal-key order depended on engine
  // implementation, not on the source-array concatenation order.
  all.sort((a, b) => {
    const ka = timelineKey(a);
    const kb = timelineKey(b);
    if (ka !== kb) return ka > kb ? -1 : 1;
    if (a.sf_last_modified !== b.sf_last_modified) {
      return a.sf_last_modified > b.sf_last_modified ? -1 : 1;
    }
    return a.id < b.id ? -1 : 1;
  });

  if (all.length === 0) {
    return renderCollapsibleRightCard({
      card: "timeline",
      testid: "opp-timeline",
      headerHTML: "Conversation timeline",
      bodyHTML: `<p class="empty-bucket">No conversations pulled yet — Refetch &amp; Reclassify to populate.</p>`,
    });
  }
  const counts = `${d.tasks.length}t · ${d.chatter.length}c · ${d.events.length}e · ${d.notes.length}n · ${d.emails.length}m`;
  return renderCollapsibleRightCard({
    card: "timeline",
    testid: "opp-timeline",
    headerHTML: `Conversation timeline <span class="row-count">${all.length} entries · ${esc(counts)}</span>`,
    bodyHTML: `
      <ul class="timeline" style="list-style:none; padding:0; margin:0;">
        ${all.map(renderTimelineEntry).join("")}
      </ul>`,
  });
}

// SGNL-101 — executive-summary rollup of the conversation timeline.
// Renders above the raw timeline section. Three states:
//   - `null` (no source rows): empty-state placeholder, no LLM call fired.
//   - loading: placeholder spinner inserted by `renderShell` and
//     replaced by `hydrateRollup` once the fetch resolves.
//   - loaded: themes + last-activity + next-step + stale indicator if
//     `generated_at` < `source_max_modified` (a new source row landed
//     after generation; next request will refresh).
function renderRollupSection(rollup: ConversationRollup | null): string {
  if (!rollup) {
    return renderCollapsibleRightCard({
      card: "conversation-rollup",
      testid: "opp-conversation-rollup",
      headerHTML: "What's been happening",
      bodyHTML: `<p class="empty-bucket">No conversations yet — Refetch &amp; Reclassify to populate.</p>`,
    });
  }
  if (rollup.themes.length === 0) {
    return renderCollapsibleRightCard({
      card: "conversation-rollup",
      testid: "opp-conversation-rollup",
      headerHTML: "What's been happening",
      bodyHTML: `<p class="muted">No substantive themes inferred from the current sources.</p>`,
    });
  }
  const lastActivity = rollup.last_activity_at
    ? esc(fmtDate(rollup.last_activity_at))
    : "—";
  const nextStep = rollup.next_step ? esc(rollup.next_step) : null;
  // CR Q6 on PR #58: parse to Date before comparing. Both fields are
  // ISO strings but `>` on raw strings is only correct when both are in
  // strict UTC ISO form ('Z' suffix). Anything else (FE-local TZ offset
  // like '+00:00', sub-second precision differences) breaks lexical
  // ordering. Date() parse normalises both sides.
  const generatedMs = new Date(rollup.generated_at).getTime();
  const sourceMaxMs = new Date(rollup.source_max_modified).getTime();
  const stale =
    Number.isFinite(generatedMs) &&
    Number.isFinite(sourceMaxMs) &&
    sourceMaxMs > generatedMs
      ? ` <span class="muted" style="font-size:11px;">(refreshing on next view)</span>`
      : "";
  const generatedAt = esc(fmtDateTime(rollup.generated_at));
  return renderCollapsibleRightCard({
    card: "conversation-rollup",
    testid: "opp-conversation-rollup",
    headerHTML: `What's been happening
      <span class="row-count">last activity ${lastActivity}${stale}</span>`,
    bodyHTML: `
      <ol style="padding-left:20px; margin:0 0 12px;" data-testid="opp-rollup-themes">
        ${rollup.themes.map((t) => `<li style="margin-bottom:4px;">${esc(t)}</li>`).join("")}
      </ol>
      ${
        nextStep
          ? `<div style="padding:8px 12px; background:var(--bg, #f8f9fa); border-radius:4px; margin-bottom:8px;" data-testid="opp-rollup-next-step">
              <strong>Next step:</strong> ${nextStep}
            </div>`
          : ""
      }
      <div class="muted" style="font-size:11px;">
        Generated ${generatedAt}${rollup.cache_hit ? " · cached" : ""}
      </div>`,
  });
}


// ---- Similar deals panel (SGNL-144 / Epic A PR-2) -----------------------------
//
// Renders the top-5 opps most similar to the current one by classifier-
// summary embedding. Server pre-ranks with sort_by=won_value: wins-by-
// amount first, then losses-by-amount, with the closer ANN match
// breaking ties. The panel surface helps the AE walk in with concrete
// precedent — "deals like this won by sharing the GenAI roadmap deck."

// Loading placeholder that's swapped in before the fetch starts so the
// slot is visible during page render. Replaced by hydrateSimilarOpps
// once the API responds.
function renderSimilarOppsLoading(): string {
  return renderCollapsibleRightCard({
    card: "similar-deals",
    testid: "opp-similar-deals",
    headerHTML: "Similar deals",
    bodyHTML: `<p class="muted" data-testid="opp-similar-deals-loading">Loading similar deals…</p>`,
  });
}

function renderSimilarOppsEmpty(): string {
  // Two empty paths: (a) opp has no embedding yet (classifier hasn't
  // produced a summary, or embedder hasn't run since), (b) opp has an
  // embedding but no closed candidates qualify. Both surface the same
  // message — the FE doesn't need to distinguish; the data will catch
  // up after the next 07:00 UTC cron.
  return renderCollapsibleRightCard({
    card: "similar-deals",
    testid: "opp-similar-deals-empty",
    headerHTML: "Similar deals",
    bodyHTML: `<p class="empty-bucket">No similar deals available yet. The embedding pass runs daily at 07:00 UTC — re-check tomorrow morning.</p>`,
  });
}

function renderSimilarOppsError(msg: string): string {
  return renderCollapsibleRightCard({
    card: "similar-deals",
    testid: "opp-similar-deals-error",
    headerHTML: "Similar deals",
    bodyHTML: `<p class="muted">Couldn't load similar deals (${esc(msg)}).</p>`,
  });
}

function renderSimilarOppRow(row: SimilarOppRow): string {
  // Outcome chip. UI review F-01 (PR #75): the codebase chip palette
  // is space-separated `chip <colour>` (e.g. `chip blue`), not the
  // hyphenated `chip-won` form. styles.css defines aqua / coral /
  // magenta / indigo / blue. Won = blue (already commented as
  // "neutral-info / Won"); Lost = coral ("bad / Lost"); Open = indigo
  // (neutral info). UI review F-03 (PR #75): visible label + aria
  // attributes so screen readers announce the outcome, not the glyph.
  let outcomeChip: string;
  if (row.is_won === true) {
    outcomeChip =
      `<span class="chip blue" aria-label="Closed Won">` +
      `<span aria-hidden="true">✓</span> Won</span>`;
  } else if (isClosedLost(row)) {
    outcomeChip =
      `<span class="chip coral" aria-label="Closed Lost">` +
      `<span aria-hidden="true">✗</span> Lost</span>`;
  } else {
    const stageLabel = row.stage ?? "Open";
    outcomeChip = `<span class="chip indigo" aria-label="${esc(stageLabel)}">${esc(stageLabel)}</span>`;
  }
  const amount = fmtMoney(row.amount);
  const close = row.close_date ? esc(row.close_date) : "—";
  const account = row.account_name ?? "—";
  const owner = row.owner_name ?? "—";
  // AI maturity uses the existing `chip indigo` "info" colour — same
  // palette token, distinguishable from the outcome chip by position
  // (later in the row).
  const maturity = row.ai_maturity ? `<span class="chip indigo">${esc(row.ai_maturity)}</span>` : "";
  // UI review F-06 (PR #75): convert raw cosine distance (0=identical,
  // 2=opposite) to a human-readable "match score" %. AEs scan the
  // panel; "92% match" is immediately legible where "dist 0.16" is
  // not. Math: 1 - (distance / 2) → [0, 1] then ×100. Clamp to [0, 100]
  // so any out-of-band distance doesn't surface a negative percentage.
  //
  // PR-Agent #3231677464 (PR #75): defend against null / undefined /
  // NaN / Infinity. `cosine_distance` is typed `number` but a future
  // schema change (or a malformed API response in a dev environment)
  // could surface non-finite. Falling through to "?% match" beats
  // crashing the row render or emitting "NaN% match".
  const matchScoreLabel = Number.isFinite(row.cosine_distance)
    ? `${Math.max(0, Math.min(100, Math.round((1 - row.cosine_distance / 2) * 100)))}% match`
    : "—";
  // Linkify the opp name so a click drops into the opp detail page.
  // Hash-router pattern matches the existing #opp/{id} convention.
  const oppHref = `#opp/${encodeURIComponent(row.opp_id)}`;
  const excerpt = row.summary_excerpt
    ? `<p class="muted" style="margin:6px 0 0;">${esc(row.summary_excerpt)}</p>`
    : "";
  // UI review F-04 + F-05 (PR #75):
  // - Name on line 1 (primary reading order). Outcome chip + match
  //   score travel WITH the name on the same line but the strong
  //   tag earns first scan.
  // - Border uses `var(--line)` (the canonical light-mode token) not
  //   the undefined `--border-muted` (which had a dark-mode fallback
  //   #2a2a2a that would have shown up as a heavy black bar in light
  //   mode).
  return `
    <li class="similar-deal-row" data-testid="opp-similar-deal-row" data-opp-id="${esc(row.opp_id)}" style="border-top:1px solid var(--line); padding:10px 0;">
      <div style="display:flex; gap:8px; align-items:baseline; flex-wrap:wrap;">
        <a href="${oppHref}" data-testid="opp-similar-deal-link"><strong>${esc(row.name)}</strong></a>
        ${outcomeChip}
        <span class="muted" style="margin-left:auto; font-size:0.85em;" title="How closely this opp's classifier summary matches the current opp (100% = identical)">${matchScoreLabel}</span>
      </div>
      <div class="muted" style="font-size:0.9em; margin-top:4px; display:flex; gap:8px; flex-wrap:wrap; align-items:baseline;">
        <span>${esc(account)}</span>
        <span>·</span>
        <span>${amount}</span>
        <span>·</span>
        <span>closed ${close}</span>
        ${maturity ? `<span>·</span>${maturity}` : ""}
        <span>·</span>
        <span>owner: ${esc(owner)}</span>
      </div>
      ${excerpt}
    </li>`;
}

// SGNL-212: top-N truncation for Similar Deals. Some opps surface 100+
// matches and scrolling past the entire list buries the cards below.
// Render the top 5 by current rank (wins-first-by-value — NOT by date;
// PR-review CR HIGH corrected the JIRA description's "older" wording)
// and stash the rest behind a "Show N more" toggle. No network round-
// trip — the full payload is already in the DOM, just hidden.
const SIMILAR_DEALS_INITIAL_VISIBLE = 5;

function renderSimilarOppsCard(resp: SimilarOppsResponse): string {
  if (resp.similar.length === 0) {
    return renderSimilarOppsEmpty();
  }
  const total = resp.similar.length;
  const cap = SIMILAR_DEALS_INITIAL_VISIBLE;
  const initial = resp.similar.slice(0, cap);
  const overflow = resp.similar.slice(cap);
  const headerCount = `<span class="row-count" title="ranked: wins by value, losses by value, then closest ANN match">${total} match${total === 1 ? "" : "es"}</span>`;
  // Header now includes the total count so operators see depth even
  // when the card is collapsed via the SGNL-203 <details> wrapper.
  const headerHTML = `Similar deals (${total}) ${headerCount}`;
  // PR-review (a11y MED + CR LOW): keep the button in the DOM, toggle
  // `aria-expanded` + the label, instead of self-destruct-on-click.
  // Reveal-once focus was lost (the destroyed button left focus on
  // <body>); the toggle pattern keeps focus anchored and lets the
  // operator collapse again. `aria-controls` ties the button to the
  // overflow region for screen-reader navigation.
  //
  // PR-Agent #128 (importance 6): the overflow container must be a
  // <ul> because renderSimilarOppRow returns <li> — putting <li>
  // children inside a <div> is invalid HTML and weakens list a11y.
  const overflowHTML = overflow.length > 0
    ? `
      <ul id="opp-similar-deals-overflow"
          class="similar-deals-overflow similar-deals-list"
          data-testid="opp-similar-deals-overflow"
          style="list-style:none; padding:0; margin:0;"
          hidden>
        ${overflow.map(renderSimilarOppRow).join("\n")}
      </ul>
      <button type="button" class="action-link opp-similar-deals-toggle"
              data-action="similar-deals-show-more"
              data-testid="opp-similar-deals-show-more"
              aria-expanded="false"
              aria-controls="opp-similar-deals-overflow"
              data-label-more="Show ${overflow.length} more"
              data-label-fewer="Show fewer">
        Show ${overflow.length} more
      </button>`
    : "";
  return renderCollapsibleRightCard({
    card: "similar-deals",
    testid: "opp-similar-deals",
    headerHTML,
    bodyHTML: `
      <p class="muted" style="margin:0 0 4px;">Closed opps with the most similar classifier summary. Wins first, then losses, ranked by deal value within each group.</p>
      <ul class="similar-deals-list" data-testid="opp-similar-deals-list" style="list-style:none; padding:0; margin:8px 0 0;">
        ${initial.map(renderSimilarOppRow).join("\n")}
      </ul>
      ${overflowHTML}`,
  });
}

// ---- Recommended POCs card (SGNL-146) -------------------------------------

function renderRecommendedPocsLoading(): string {
  return renderCollapsibleRightCard({
    card: "recommended-pocs",
    testid: "opp-recommended-pocs",
    headerHTML: "Recommended POCs",
    bodyHTML: `<p class="muted" data-testid="opp-recommended-pocs-loading">Loading recommended POCs…</p>`,
  });
}

// SGNL-212: Refresh / Retry buttons live in the <summary> via
// summaryActionHTML so they're reachable when the card is collapsed.
// data-stop-summary-toggle wrapper + bindCardSummaryActions handler
// prevent the click from also toggling the <details>.
const REFRESH_POCS_BTN = `<button class="action-link" data-action="refresh-recommended-pocs" type="button">Refresh</button>`;

function renderRecommendedPocsEmpty(): string {
  // Empty when the opp has no classifier summary yet (LLM has nothing
  // to match against) OR the LLM judged that no POC was a meaningful
  // match. Same message — operator doesn't need to distinguish.
  return renderCollapsibleRightCard({
    card: "recommended-pocs",
    testid: "opp-recommended-pocs-empty",
    headerHTML: "Recommended POCs",
    summaryActionHTML: REFRESH_POCS_BTN,
    bodyHTML: `<p class="empty-bucket">No recommended POCs yet. The matcher runs after the opp is classified — click Refresh to retry once the classifier has produced a summary.</p>`,
  });
}

function renderRecommendedPocsError(msg: string): string {
  return renderCollapsibleRightCard({
    card: "recommended-pocs",
    testid: "opp-recommended-pocs-error",
    headerHTML: "Recommended POCs",
    summaryActionHTML: REFRESH_POCS_BTN,
    bodyHTML: `<p class="muted">Couldn't load recommended POCs (${esc(msg)}).</p>`,
  });
}

function renderRecommendedPocRow(pick: PocPick): string {
  // Match score chip — same `chip indigo` palette token as the Similar
  // deals card uses for "info" / neutral data, so the row visually
  // mirrors the Similar deals shape an AE has already learned.
  const pctChip = `<span class="chip indigo" title="LLM-assessed strength of fit, 0-100">${pick.match_pct}%</span>`;
  const nameNode = pick.url
    ? `<a href="${esc(pick.url)}" target="_blank" rel="noopener noreferrer"><strong>${esc(pick.name)}</strong></a>`
    : `<strong>${esc(pick.name)}</strong>`;
  const rationale = pick.rationale
    ? `<p class="muted" style="margin:6px 0 0;">${esc(pick.rationale)}</p>`
    : "";
  // SGNL-149: Draft Message CTA. Click → POST /draft-message → modal
  // opens with editable textarea + copy-to-clipboard button. POC name
  // stashed in data-poc-name so the modal title can show it without
  // re-fetching catalog metadata.
  const draftBtn = `<button class="action-link" data-action="poc-draft-message" data-poc-id="${esc(pick.poc_id)}" data-poc-name="${esc(pick.name)}" type="button">Draft message</button>`;
  return `
    <li class="recommended-poc-row" data-testid="opp-recommended-poc-row" data-poc-id="${esc(pick.poc_id)}" style="border-top:1px solid var(--line); padding:10px 0;">
      <div style="display:flex; gap:8px; align-items:baseline; flex-wrap:wrap;">
        ${nameNode}
        ${pctChip}
        <span style="margin-left:auto;">${draftBtn}</span>
      </div>
      ${rationale}
    </li>`;
}

function renderRecommendedPocsCard(resp: PocRecommendationsOut | null): string {
  if (!resp || resp.picks.length === 0) {
    return renderRecommendedPocsEmpty();
  }
  return renderCollapsibleRightCard({
    card: "recommended-pocs",
    testid: "opp-recommended-pocs",
    headerHTML: `Recommended POCs <span class="row-count" title="LLM-ranked, computed on first view or after a Refresh">${resp.picks.length} match${resp.picks.length === 1 ? "" : "es"}</span>`,
    summaryActionHTML: REFRESH_POCS_BTN,
    bodyHTML: `
      <p class="muted" style="margin:0 0 4px;">Internal POCs most likely to land based on this opp's summary, identified pain, and stack.</p>
      <ul class="recommended-pocs-list" data-testid="opp-recommended-pocs-list" style="list-style:none; padding:0; margin:8px 0 0;">
        ${resp.picks.map(renderRecommendedPocRow).join("\n")}
      </ul>`,
  });
}

// ---- SGNL-27: Document Evidence card ----------------------------------------
//
// Per-attachment tag verdicts from the classifier, with two conflict surfaces:
//  - tag_conflicts: tags where the attachment majority disagrees with the
//    opp-level aggregated verdict (e.g. most docs say AI:Solution=no but
//    opp says yes)
//  - rep_llm_conflict: rep said Non-AI but one or more docs say yes on an AI tag

function renderDocumentEvidenceLoading(): string {
  return renderCollapsibleRightCard({
    card: "document-evidence",
    testid: "opp-document-evidence",
    headerHTML: "Document Evidence",
    bodyHTML: `<p class="muted" data-testid="opp-document-evidence-loading">Loading document evidence…</p>`,
  });
}

function renderDocumentEvidenceEmpty(): string {
  return renderCollapsibleRightCard({
    card: "document-evidence",
    testid: "opp-document-evidence-empty",
    headerHTML: "Document Evidence",
    bodyHTML: `<p class="empty-bucket">No classified attachments yet. Run the classifier to populate this panel.</p>`,
  });
}

function renderDocumentEvidenceError(msg: string): string {
  return renderCollapsibleRightCard({
    card: "document-evidence",
    testid: "opp-document-evidence-error",
    headerHTML: "Document Evidence",
    bodyHTML: `<p class="muted">Couldn't load document evidence (${esc(msg)}).</p>`,
  });
}

function renderDocumentEvidenceCard(result: DocumentEvidenceResult): string {
  if (result.attachments.length === 0) {
    return renderDocumentEvidenceEmpty();
  }

  const conflictBanner = result.rep_llm_conflict
    ? `<div style="padding:8px 12px; border-left:3px solid var(--coral); background:var(--bg); margin-bottom:12px;" data-testid="opp-rep-llm-conflict-banner">
        <strong>Rep vs LLM conflict:</strong> Rep tagged this as Non-AI but attachment analysis found AI:Solution or AI:Delivery evidence.
       </div>`
    : "";

  const conflictsHtml =
    result.tag_conflicts.length > 0
      ? `<div style="margin-bottom:16px;" data-testid="opp-tag-conflicts">
          <p class="muted" style="margin:0 0 6px; font-size:0.85em; text-transform:uppercase; letter-spacing:0.04em;">Tag conflicts — doc majority vs opp verdict</p>
          <table class="data-table">
            <thead>
              <tr>
                <th>Tag</th>
                <th>Opp says</th>
                <th>Docs say</th>
                <th style="white-space:nowrap;">Yes / No / Maybe</th>
              </tr>
            </thead>
            <tbody>
              ${result.tag_conflicts
                .map(
                  (c) => `
              <tr data-testid="opp-tag-conflict-row">
                <td>${esc(c.label)}</td>
                <td><span class="${chipClassForVerdict(c.opp_verdict ?? "")}">${esc(c.opp_verdict ?? "—")}</span></td>
                <td><span class="${chipClassForVerdict(c.doc_majority_verdict)}">${esc(c.doc_majority_verdict)}</span></td>
                <td class="muted" style="font-size:0.85em; white-space:nowrap;">${esc(String(c.doc_yes_count))} · ${esc(String(c.doc_no_count))} · ${esc(String(c.doc_maybe_count))}</td>
              </tr>`,
                )
                .join("")}
            </tbody>
          </table>
        </div>`
      : "";

  const attachmentsHtml = result.attachments
    .map(
      (att) => {
        const chips = att.tag_results
          .map(
            (tr) =>
              `<span class="${chipClassForVerdict(tr.verdict)}"
                     title="${esc(tr.rationale ?? "")}"
                     data-testid="opp-evidence-tag-chip">
                ${esc(tr.label)} · ${esc(tr.verdict)} · ${Number.isFinite(tr.confidence) ? `${(tr.confidence * 100).toFixed(0)}%` : "—"}
               </span>`,
          )
          .join(" ");
        const contentType = att.content_type
          ? ` <span class="muted" style="font-size:0.85em; margin-left:6px;">${esc(att.content_type)}</span>`
          : "";
        const body = att.tag_results.length > 0
          ? `<div style="margin:8px 0 0 12px; display:flex; flex-wrap:wrap; gap:4px;">${chips}</div>`
          : `<p class="muted" style="margin:6px 0 0 12px; font-size:0.85em;">No tag verdicts recorded for this attachment.</p>`;
        return `
        <li style="border-top:1px solid var(--line); padding:10px 0;"
            data-testid="opp-evidence-attachment-row"
            data-attachment-id="${esc(att.attachment_id)}">
          <details>
            <summary style="cursor:pointer; list-style:none;">
              <strong>${esc(att.filename)}</strong>${contentType}
            </summary>
            ${body}
          </details>
        </li>`;
      },
    )
    .join("");

  const count = result.attachments.length;
  return renderCollapsibleRightCard({
    card: "document-evidence",
    testid: "opp-document-evidence",
    headerHTML: `Document Evidence <span class="row-count">${count} attachment${count === 1 ? "" : "s"}</span>`,
    bodyHTML: `
      ${conflictBanner}
      ${conflictsHtml}
      <ul style="list-style:none; padding:0; margin:0;" data-testid="opp-evidence-attachments-list">
        ${attachmentsHtml}
      </ul>`,
  });
}

async function hydrateDocumentEvidence(
  host: HTMLElement,
  oppId: string,
  generation: string,
): Promise<void> {
  const slot = host.querySelector<HTMLElement>('[data-testid="opp-evidence-slot"]');
  if (!slot) return;
  try {
    const result = await fetchDocumentEvidence(oppId);
    const slotNow = host.querySelector<HTMLElement>('[data-testid="opp-evidence-slot"]');
    if (!slotNow || slotNow.dataset.evidenceGeneration !== generation) return;
    slotNow.innerHTML = renderDocumentEvidenceCard(result);
  } catch (err) {
    const slotNow = host.querySelector<HTMLElement>('[data-testid="opp-evidence-slot"]');
    if (!slotNow || slotNow.dataset.evidenceGeneration !== generation) return;
    slotNow.innerHTML = renderDocumentEvidenceError(err instanceof Error ? err.message : String(err));
  }
}

// ---- SGNL-72: Sentiment trajectory panel ------------------------------------
//
// Per-thread scatter chart: X = date, Y = sentiment (-1..+1), colour = direction.
// Loaded lazily on first visit; the BE scores unscored messages inline.

function renderSentimentLoading(): string {
  return renderCollapsibleRightCard({
    card: "sentiment",
    testid: "opp-sentiment-trajectory",
    headerHTML: "Sentiment trajectory",
    bodyHTML: `<p class="muted" data-testid="opp-sentiment-loading">Analysing conversation sentiment…</p>`,
  });
}

function renderSentimentEmpty(): string {
  return renderCollapsibleRightCard({
    card: "sentiment",
    testid: "opp-sentiment-trajectory-empty",
    headerHTML: "Sentiment trajectory",
    bodyHTML: `<p class="empty-bucket">No scored messages yet. The scorer runs on first view — check back in a moment.</p>`,
  });
}

// Sentiment Refresh / Retry shares the same data-action regardless of
// card state (loading / empty / populated / error).
const REFRESH_SENTIMENT_BTN = `<button class="action-link" data-action="refresh-sentiment" data-testid="sentiment-refresh-btn" type="button">↺ Refresh</button>`;

function renderSentimentError(msg: string): string {
  return renderCollapsibleRightCard({
    card: "sentiment",
    testid: "opp-sentiment-trajectory-error",
    headerHTML: "Sentiment trajectory",
    summaryActionHTML: `<button class="action-link" data-action="refresh-sentiment" type="button">Retry</button>`,
    bodyHTML: `<p class="muted">Couldn't load sentiment trajectory (${esc(msg)}).</p>`,
  });
}

function renderSentimentCard(result: ThreadsOut, canvasId: string): string {
  const totalMessages = result.threads.reduce((n, t) => n + t.messages.length, 0);
  const scoredMessages = result.threads.reduce(
    (n, t) => n + t.messages.filter((m) => m.sentiment_score !== null).length,
    0,
  );
  const unscoredNote =
    scoredMessages < totalMessages
      ? ` <span class="muted" style="font-size:0.85em;">(${totalMessages - scoredMessages} unscored — refresh to load more)</span>`
      : "";

  const headerCount = `<span class="row-count">${result.threads.length} thread${result.threads.length === 1 ? "" : "s"} · ${scoredMessages}/${totalMessages} scored${unscoredNote}</span>`;
  return renderCollapsibleRightCard({
    card: "sentiment",
    testid: "opp-sentiment-trajectory",
    headerHTML: `Sentiment trajectory ${headerCount}`,
    summaryActionHTML: REFRESH_SENTIMENT_BTN,
    bodyHTML: `
      <div style="position:relative; height:260px; margin-bottom:16px;">
        <canvas id="${esc(canvasId)}" data-testid="opp-sentiment-canvas"></canvas>
      </div>
      <div data-testid="opp-sentiment-threads">
        ${result.threads
          .map(
            (t) => `
          <details style="margin-bottom:8px;" data-testid="opp-sentiment-thread">
            <summary style="cursor:pointer; list-style:none;">
              <strong>${esc(t.subject ?? "(no subject)")}</strong>
              <span class="muted" style="font-size:0.85em; margin-left:6px;">${t.messages.length} message${t.messages.length === 1 ? "" : "s"}</span>
            </summary>
            <ul style="list-style:none; padding:0 0 0 12px; margin:4px 0 0;" data-testid="opp-sentiment-thread-messages">
              ${t.messages
                .map(
                  (m) => `
                <li style="border-top:1px solid var(--line); padding:6px 0;" data-testid="opp-sentiment-message">
                  <div style="display:flex; gap:8px; align-items:baseline; flex-wrap:wrap;">
                    <span class="muted" style="font-size:0.85em;">${esc(fmtDate(m.date))}</span>
                    <span class="muted" style="font-size:0.85em;">${esc(m.direction)}</span>
                    ${
                      m.sentiment_score !== null
                        ? `<span class="${m.sentiment_score >= 0.3 ? "chip aqua" : m.sentiment_score <= -0.3 ? "chip coral" : "chip"}"
                               data-testid="opp-sentiment-score-chip">
                             ${m.sentiment_score >= 0 ? "+" : ""}${m.sentiment_score.toFixed(2)}
                           </span>`
                        : `<span class="muted" style="font-size:0.85em;">unscored</span>`
                    }
                    <span style="font-size:0.9em;">${esc(m.subject ?? "(no subject)")}</span>
                  </div>
                  ${m.sentiment_reason ? `<p class="muted" style="margin:4px 0 0 0; font-size:0.85em;">${esc(m.sentiment_reason)}</p>` : ""}
                </li>`,
                )
                .join("")}
            </ul>
          </details>`,
          )
          .join("")}
      </div>`,
  });
}

function _buildSentimentPoints(result: ThreadsOut): SentimentPoint[] {
  const points: SentimentPoint[] = [];
  for (const thread of result.threads) {
    for (const msg of thread.messages) {
      if (msg.sentiment_score === null) continue;
      points.push({
        date: msg.date,
        score: msg.sentiment_score,
        direction: msg.direction as SentimentPoint["direction"],
        label: msg.subject ?? "(no subject)",
        reason: msg.sentiment_reason,
      });
    }
  }
  return points.sort((a, b) => a.date.localeCompare(b.date));
}

// Scoped per host element so simultaneous detail panels don't share state (Q-05).
const _sentimentChartDestroy = new WeakMap<HTMLElement, () => void>();

async function hydrateSentimentTrajectory(
  host: HTMLElement,
  oppId: string,
  generation: string,
  force = false,
): Promise<void> {
  const slot = host.querySelector<HTMLElement>('[data-testid="opp-sentiment-slot"]');
  if (!slot) return;
  try {
    const result = await fetchThreads(oppId, { force });
    const slotNow = host.querySelector<HTMLElement>('[data-testid="opp-sentiment-slot"]');
    if (!slotNow || slotNow.dataset.sentimentGeneration !== generation) return;

    const points = _buildSentimentPoints(result);
    // Destroy any prior chart for this host before replacing slot content —
    // on empty/error paths the canvas disappears so Chart.js must be
    // cleaned up first to avoid canvas reuse errors (PR-Agent importance 6).
    _sentimentChartDestroy.get(host)?.();
    _sentimentChartDestroy.delete(host);
    // Empty when no messages have scores yet (Q-04: simplified condition).
    if (points.length === 0) {
      slotNow.innerHTML = renderSentimentEmpty();
      return;
    }

    const canvasId = `sentiment-chart-${generation}`;
    slotNow.innerHTML = renderSentimentCard(result, canvasId);

    // Chart was already destroyed above; now create the new one.
    const destroy = drawSentimentChart(canvasId, points);
    _sentimentChartDestroy.set(host, destroy);
  } catch (err) {
    const slotNow = host.querySelector<HTMLElement>('[data-testid="opp-sentiment-slot"]');
    if (!slotNow || slotNow.dataset.sentimentGeneration !== generation) return;
    slotNow.innerHTML = renderSentimentError(
      err instanceof Error ? err.message : String(err),
    );
  }
}

const _sentimentRefreshAbort = new WeakMap<HTMLElement, AbortController>();

function bindRefreshSentiment(host: HTMLElement, oppId: string): void {
  _sentimentRefreshAbort.get(host)?.abort();
  const controller = new AbortController();
  _sentimentRefreshAbort.set(host, controller);
  host.addEventListener(
    "click",
    (e) => {
      const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(
        '[data-action="refresh-sentiment"]',
      );
      if (!btn) return;
      e.preventDefault();
      const slot = host.querySelector<HTMLElement>('[data-testid="opp-sentiment-slot"]');
      if (!slot) return;
      const newGen =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;
      slot.dataset.sentimentGeneration = newGen;
      slot.innerHTML = renderSentimentLoading();
      btn.disabled = true;
      void (async () => {
        try {
          await hydrateSentimentTrajectory(host, oppId, newGen, true);
        } finally {
          const b = host.querySelector<HTMLButtonElement>('[data-action="refresh-sentiment"]');
          if (b) b.disabled = false;
        }
      })();
    },
    { signal: controller.signal },
  );
}

// ---- Top-level render ------------------------------------------------------

// ---- SGNL-218: Lost Deal Analysis card -----------------------------------
//
// Surfaces on the opp-detail page ONLY when the opp closed lost. Pulls
// from `d.history` (already on the wire) + the global bundle's
// `pre_close_max_stage` distribution for the rarity callout. Pure
// FE — no new BE endpoint.
//
// Layout: header row of 4 chip-style insight callouts (time-to-loss,
// stalled-in-stage, abrupt-loss, rarity) above a sortable stage-journey
// table. Each callout is conditional — only renders when its compute
// helper returns non-null.

// SGNL-218 — pre-PR architect MED: widen the duck type so the same
// predicate can be reused by `renderSimilarOppRow` (which receives the
// slim SimilarOppRow shape, not OpportunityDetailFull). Single source of
// truth for the closed-lost predicate across the file.
function isClosedLost(d: { is_closed: boolean | null; is_won: boolean | null }): boolean {
  return d.is_closed === true && d.is_won === false;
}

// SGNL-218 — pre-PR a11y review (code-reviewer MED): every chip carries
// an aria-label so screen readers announce a complete unit rather than
// running together. The chip strip itself gets role="list" and each
// chip role="listitem" so the grouping is communicated.

function renderTimeToLossChip(days: number | null): string {
  if (days === null) return "";
  // Coral if > 365 (deal lingered a year+), slate otherwise. Both use
  // the Bounteous tinted-chip palette.
  const tone = days > 365 ? "coral" : "slate";
  const label = `Time to loss: ${days.toLocaleString()} days`;
  return `
    <div class="lost-intel-chip lost-intel-chip--${tone}"
         role="listitem" aria-label="${esc(label)}"
         data-testid="lost-intel-time-to-loss">
      <div class="lost-intel-chip-label">Time to loss</div>
      <div class="lost-intel-chip-value">${days.toLocaleString()} days</div>
    </div>`;
}

function renderStalledChip(stage: string, days: number): string {
  const label = `Stalled in stage ${stage} for ${days.toLocaleString()} days`;
  return `
    <div class="lost-intel-chip lost-intel-chip--coral"
         role="listitem" aria-label="${esc(label)}"
         data-testid="lost-intel-stalled">
      <div class="lost-intel-chip-label">Stalled in stage</div>
      <div class="lost-intel-chip-value">${esc(stage)}</div>
      <div class="lost-intel-chip-detail">${days.toLocaleString()} days</div>
    </div>`;
}

function renderAbruptChip(stage: string, prob: number, gap: number): string {
  const dayWord = gap === 1 ? "day" : "days";
  const label = `Abrupt loss from ${stage} at ${prob.toFixed(0)} percent, lost in ${gap} ${dayWord}`;
  return `
    <div class="lost-intel-chip lost-intel-chip--magenta"
         role="listitem" aria-label="${esc(label)}"
         data-testid="lost-intel-abrupt">
      <div class="lost-intel-chip-label">Abrupt loss</div>
      <div class="lost-intel-chip-value">${esc(stage)} · ${prob.toFixed(0)}%</div>
      <div class="lost-intel-chip-detail">→ lost in ${gap.toLocaleString()} ${dayWord}</div>
    </div>`;
}

function renderRarityChip(percentage: number, cohortSize: number, peakStage: string): string {
  // Lower percentage = rarer outcome. Surface as aqua ("good signal" tint)
  // when < 15% (truly rare) and slate otherwise.
  const tone = percentage < 15 ? "aqua" : "slate";
  const label = `Peak-stage rarity: ${percentage.toFixed(1)} percent of lost deals reached ${peakStage} (cohort of ${cohortSize.toLocaleString()})`;
  return `
    <div class="lost-intel-chip lost-intel-chip--${tone}"
         role="listitem" aria-label="${esc(label)}"
         data-testid="lost-intel-rarity">
      <div class="lost-intel-chip-label">Peak-stage rarity</div>
      <div class="lost-intel-chip-value">${percentage.toFixed(1)}% of lost deals</div>
      <div class="lost-intel-chip-detail">
        reached ${esc(peakStage)} (cohort: ${cohortSize.toLocaleString()})
      </div>
    </div>`;
}

function renderStageTimelineTable(timeline: readonly StageTimelineEntry[]): string {
  if (timeline.length === 0) {
    return `<p class="empty-bucket" data-testid="lost-intel-timeline-empty">
      No non-terminal stage history on file for this opp.
    </p>`;
  }
  return `
    <table class="data-table" data-testid="lost-intel-timeline-table">
      <thead>
        <tr>
          <th>Stage</th>
          <th>Entered</th>
          <th class="num">Days in stage</th>
          <th class="num">Probability</th>
          <th class="num">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${timeline
          .map(
            (t) => `
          <tr data-testid="lost-intel-timeline-row" data-stage="${esc(t.stage)}">
            <td>${esc(t.stage)}</td>
            <td>${esc(t.enteredAt ?? "—")}</td>
            <td class="num">${t.daysInStage !== null ? t.daysInStage.toLocaleString() : "—"}</td>
            <td class="num">${
              typeof t.probability === "number" && Number.isFinite(t.probability)
                ? esc(`${t.probability.toFixed(0)}%`)
                : "—"
            }</td>
            <td class="num">${
              typeof t.amount === "number" && Number.isFinite(t.amount) ? fmtMoney(t.amount) : "—"
            }</td>
          </tr>`,
          )
          .join("")}
      </tbody>
    </table>`;
}

function renderLostDealCard(d: OpportunityDetailFull): string {
  if (!isClosedLost(d)) return "";

  // `d.history ?? []` — defensive against the (rare) case where the BE
  // schema returns `history: null/undefined`. PR-Agent #137 imp 5.
  const timeline = computeStageTimeline(d.history ?? []);
  const ttl = computeTimeToLoss(d.created_date, d.close_date);
  // Pass close_date so the FINAL non-terminal stage's dwell can be
  // computed against it (pre-PR code-reviewer MED — the most common
  // stall pattern is the final pre-close stage; without this, the chip
  // never fires for that case).
  const stalled = computeStalledStage(timeline, undefined, d.close_date);
  const abrupt = computeAbruptLoss(timeline, d.close_date);
  // Trim `pre_close_max_stage` ONCE and reuse for both the rarity
  // computation and the chip rendering — avoids rendering whitespace-
  // padded stage names from SF history rows. PR-Agent #137 imp 5.
  // Rarity needs the global bundle for the cohort denominator; getState()
  // is safe here — opp-detail is reached only after the bundle has loaded
  // (the root route waits for /api/data before mounting the detail page).
  const peakStage =
    typeof d.pre_close_max_stage === "string" ? d.pre_close_max_stage.trim() : "";
  const rarity = peakStage
    ? computeStagePeakRarity(getState().data?.opps ?? [], peakStage)
    : null;

  const chips =
    renderTimeToLossChip(ttl) +
    (stalled ? renderStalledChip(stalled.stage, stalled.days) : "") +
    (abrupt
      ? renderAbruptChip(abrupt.peakStage, abrupt.peakProbability, abrupt.daysFromPeakToLoss)
      : "") +
    (rarity ? renderRarityChip(rarity.percentage, rarity.cohortSize, peakStage) : "");

  return renderCollapsibleRightCard({
    card: "lost-deal-analysis",
    testid: "opp-lost-deal-analysis",
    headerHTML: "Lost-deal analysis",
    bodyHTML: `
      ${chips ? `<div class="lost-intel-chips" role="list" aria-label="Lost-deal insight chips" data-testid="lost-intel-chips">${chips}</div>` : ""}
      <!-- SGNL-220 — LLM coaching block. Hydrated by hydrateLostCoaching()
           after the static analysis renders. Generation-stamped so a
           late-arriving fetch from a PREVIOUS render (operator hops
           opp A → opp B before A's LLM call resolves) doesn't overwrite
           the current page. Same pattern as the rollup/next-move slots. -->
      <div data-testid="opp-lost-coaching-slot" data-coaching-generation="">
        ${renderLostCoachingLoading()}
      </div>
      <!-- SGNL-221 — pattern-matches slot. Sibling of the coaching
           slot so each hydrates independently (one slow call doesn't
           block the other). Same generation-stamping pattern so a
           stale response from an opp-switch can't overwrite the
           current page. -->
      <div data-testid="opp-lost-pattern-slot" data-pattern-generation="">
        ${renderLostPatternLoading()}
      </div>
      <h4 class="lost-intel-timeline-heading">Stage journey</h4>
      ${renderStageTimelineTable(timeline)}
    `,
  });
}

// ---- SGNL-220: Lost-deal LLM coaching block ------------------------------

function renderLostCoachingLoading(): string {
  return `
    <div class="lost-coaching lost-coaching--loading" data-testid="opp-lost-coaching-loading">
      <h4 class="lost-coaching-title">Coaching insight</h4>
      <p class="muted">Generating insight…</p>
    </div>`;
}

function renderLostCoachingEmpty(): string {
  return `
    <div class="lost-coaching lost-coaching--empty" data-testid="opp-lost-coaching-empty">
      <h4 class="lost-coaching-title">Coaching insight</h4>
      <p class="muted">
        Not enough stage history to generate coaching for this deal.
      </p>
    </div>`;
}

function renderLostCoachingError(msg: string): string {
  return `
    <div class="lost-coaching lost-coaching--error" data-testid="opp-lost-coaching-error">
      <h4 class="lost-coaching-title">Coaching insight</h4>
      <p class="error">Couldn't generate coaching: ${esc(msg)}</p>
      <button type="button" class="btn-secondary" data-action="refresh-lost-coaching">
        Try again
      </button>
    </div>`;
}

function renderLostCoachingCard(c: import("../types").LostCoachingOut): string {
  const findings =
    c.key_findings.length === 0
      ? ""
      : `<div class="lost-coaching-section">
           <h5 class="lost-coaching-section-title">Key findings</h5>
           <ul class="lost-coaching-list">
             ${c.key_findings.map((f) => `<li>${esc(f)}</li>`).join("")}
           </ul>
         </div>`;
  const actions =
    c.recommended_actions.length === 0
      ? ""
      : `<div class="lost-coaching-section">
           <h5 class="lost-coaching-section-title">What we could have done</h5>
           <ul class="lost-coaching-list lost-coaching-list--actions">
             ${c.recommended_actions.map((a) => `<li>${esc(a)}</li>`).join("")}
           </ul>
         </div>`;
  const staleBadge = c.is_stale
    ? `<span class="chip slate" title="Older prompt version — coaching will regenerate on next ingest">stale</span>`
    : "";
  return `
    <div class="lost-coaching lost-coaching--populated" data-testid="opp-lost-coaching">
      <div class="lost-coaching-header">
        <h4 class="lost-coaching-title">Coaching insight</h4>
        ${staleBadge}
        <button type="button" class="btn-link lost-coaching-refresh"
                data-action="refresh-lost-coaching"
                aria-label="Regenerate coaching">
          Refresh
        </button>
      </div>
      ${c.summary ? `<p class="lost-coaching-summary">${esc(c.summary)}</p>` : ""}
      ${findings}
      ${actions}
      <p class="muted lost-coaching-provenance">
        AI-generated · ${esc(c.model)} · prompt ${esc(c.prompt_version)}
      </p>
    </div>`;
}

async function hydrateLostCoaching(
  host: HTMLElement,
  oppId: string,
  generation: string,
): Promise<void> {
  const slot = host.querySelector<HTMLElement>('[data-testid="opp-lost-coaching-slot"]');
  if (!slot) return;
  // Pre-flight: only hydrate when the static card is rendered (i.e. opp
  // is Closed Lost). renderLostDealCard already gates on this; the slot
  // won't exist otherwise.
  try {
    const coaching = await fetchLostCoaching(oppId);
    // Race-guard — operator hopped to a different opp before this
    // resolved. Skip the write.
    if (slot.dataset.coachingGeneration !== generation) return;
    if (coaching === null) {
      slot.innerHTML = renderLostCoachingEmpty();
    } else {
      slot.innerHTML = renderLostCoachingCard(coaching);
    }
  } catch (err) {
    if (slot.dataset.coachingGeneration !== generation) return;
    slot.innerHTML = renderLostCoachingError(err instanceof Error ? err.message : String(err));
  }
}

// ---- SGNL-221: Lost-deal pattern matches --------------------------------
//
// Surfaces inside the lost-intel card as a sibling of the SGNL-220
// coaching slot. Independent hydration path so one slow request
// doesn't block the other. Generation-stamped so opp-switching
// doesn't let a stale match list render into the current page.

function renderLostPatternLoading(): string {
  return `
    <div class="lost-pattern lost-pattern--loading" data-testid="opp-lost-pattern-loading">
      <h4 class="lost-pattern-title">Other deals at risk</h4>
      <p class="muted">Scanning for similar deal patterns…</p>
    </div>`;
}

function renderLostPatternEmpty(): string {
  return `
    <div class="lost-pattern lost-pattern--empty" data-testid="opp-lost-pattern-empty">
      <h4 class="lost-pattern-title">Other deals at risk</h4>
      <p class="muted">No similar deal patterns found in the corpus.</p>
    </div>`;
}

function renderLostPatternError(msg: string): string {
  return `
    <div class="lost-pattern lost-pattern--error" data-testid="opp-lost-pattern-error">
      <h4 class="lost-pattern-title">Other deals at risk</h4>
      <p class="muted">Couldn't load pattern matches: ${esc(msg)}</p>
    </div>`;
}

function renderLostPatternCard(
  out: import("../types").LostPatternMatchesResponse,
): string {
  if (out.matches.length === 0) {
    return renderLostPatternEmpty();
  }
  // PR-Agent / a11y: the stage path is the "why these are similar"
  // signal. Surface it as a small caption above the list so the
  // operator understands the basis for the match.
  const path = out.fingerprint.stage_path.map((s) => esc(s)).join(" → ");
  const dwell = out.fingerprint.longest_dwell_days;
  const dwellHint =
    dwell != null
      ? ` · longest dwell ~${dwell}d (${esc(out.fingerprint.activity_gap_bucket)})`
      : "";
  const rows = out.matches
    .map((m) => {
      // Open deals are the actionable surface — flag them with a
      // chip so the operator can spot at-risk OPEN deals at a glance.
      const atRiskChip = !m.is_closed
        ? `<span class="chip coral lost-pattern-risk-chip" data-testid="lost-pattern-at-risk-chip">at risk</span>`
        : "";
      const stageText = m.stage ? esc(m.stage) : "—";
      const amountText = m.amount != null ? esc(fmtMoney(m.amount)) : "—";
      // Open deals link to the opp detail; closed-lost deals link too
      // (operators may want to review the historic context).
      return `
        <li class="lost-pattern-row" data-testid="lost-pattern-row">
          <a class="lost-pattern-link"
             href="#opp/${esc(m.opportunity_id)}"
             data-action="lost-pattern-navigate"
             data-opp-id="${esc(m.opportunity_id)}">
            <span class="lost-pattern-name">${esc(m.name)}</span>
            ${atRiskChip}
            <span class="lost-pattern-meta">
              ${stageText} · ${amountText}
            </span>
          </a>
        </li>`;
    })
    .join("");
  return `
    <div class="lost-pattern lost-pattern--populated" data-testid="opp-lost-pattern">
      <h4 class="lost-pattern-title">Other deals at risk</h4>
      <p class="muted lost-pattern-caption" data-testid="lost-pattern-caption">
        Similar to: ${path}${dwellHint}
      </p>
      <ul class="lost-pattern-list">${rows}</ul>
    </div>`;
}

async function hydrateLostPattern(
  host: HTMLElement,
  oppId: string,
  generation: string,
): Promise<void> {
  const slot = host.querySelector<HTMLElement>('[data-testid="opp-lost-pattern-slot"]');
  if (!slot) return;
  try {
    const out = await fetchLostPatternMatches(oppId);
    // Race-guard — opp-switch before this resolved.
    if (slot.dataset.patternGeneration !== generation) return;
    slot.innerHTML = renderLostPatternCard(out);
  } catch (err) {
    if (slot.dataset.patternGeneration !== generation) return;
    slot.innerHTML = renderLostPatternError(err instanceof Error ? err.message : String(err));
  }
}

// Pre-PR code-reviewer MED: matches the established
// `_nextMoveAbort` / `_pocRefreshAbort` / `_sentimentRefreshAbort`
// pattern. Without the WeakMap+AbortController, a re-render of the
// same host (hash navigation back to the same opp, or the Refetch
// button) would stack delegated click handlers and a single Refresh
// click would fire N parallel hydrations.
const _coachingRefreshAbort = new WeakMap<HTMLElement, AbortController>();

function bindRefreshLostCoaching(host: HTMLElement, oppId: string): void {
  _coachingRefreshAbort.get(host)?.abort();
  const controller = new AbortController();
  _coachingRefreshAbort.set(host, controller);
  // Event-delegated handler on the host — both the loaded card's
  // Refresh button and the error card's Try-again button trigger a
  // re-fetch via the same data-action attribute.
  host.addEventListener(
    "click",
    (e) => {
      // PR-Agent #139 imp 3: `instanceof Element` before `.closest()` —
      // clicking a text node (or other non-Element target) would
      // otherwise throw at runtime since `.closest` only exists on
      // Element instances.
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('[data-action="refresh-lost-coaching"]')) return;
      e.preventDefault();
      const slot = host.querySelector<HTMLElement>('[data-testid="opp-lost-coaching-slot"]');
      if (!slot) return;
      const generation = String(Date.now());
      slot.dataset.coachingGeneration = generation;
      slot.innerHTML = renderLostCoachingLoading();
      void hydrateLostCoaching(host, oppId, generation);
      // SGNL-221 — refresh the pattern slot in lockstep. They share
      // the same opp context; if the operator hits "Refresh" they
      // mean "give me the current snapshot for everything".
      const patternSlot = host.querySelector<HTMLElement>(
        '[data-testid="opp-lost-pattern-slot"]',
      );
      if (patternSlot) {
        patternSlot.dataset.patternGeneration = generation;
        patternSlot.innerHTML = renderLostPatternLoading();
        void hydrateLostPattern(host, oppId, generation);
      }
    },
    { signal: controller.signal },
  );
}

function renderShell(d: OpportunityDetailFull): string {
  // Two-column body: ~32% left rail, rest right. CSS grid keeps the
  // breakpoint clean without needing a new class — inline grid-template
  // is fine for one consumer. If a second page later wants the same
  // layout we'll lift to .opp-detail-grid in styles.css.
  return `
    <div class="opp-detail" data-testid="opp-detail">
      ${renderHeader(d)}
      <div class="opp-detail-body">
        <div class="opp-detail-rail">
          ${renderSummaryCard(d)}
          ${renderMaturityCard(d)}
          ${renderTagsCard(d)}
          ${renderAdminCard(d)}
        </div>
        <div class="opp-detail-main">
          <!-- SGNL-203 / SGNL-218: right-column order.
               Order is: what's happened → how is it trending → what
               to do next → research material. High-signal "interpret +
               act" surfaces before "research" surfaces.
                 1. Conversation rollup — narrative summary
                 2. Lost-deal analysis — SGNL-218, ONLY when Closed Lost
                    (between rollup and raw timeline — rollup provides
                    narrative context FIRST, then this card interprets
                    the lost-deal pattern, then raw timeline drills in.
                    Per architect MED in pre-PR review.)
                 3. Timeline — what's happened (detail)
                 4. Sentiment trajectory — how is it trending (interpret)
                 5. Suggested next move — what to do (act)
                 6. Similar deals — research
                 7. Recommended POCs — research
                 8. Document Evidence — supporting material
          -->
          <!-- SGNL-101 rollup placeholder; replaced by hydrateRollup() -->
          <div data-testid="opp-conversation-rollup-slot">
            ${renderCollapsibleRightCard({
              card: "conversation-rollup",
              testid: "opp-conversation-rollup-loading",
              headerHTML: "What's been happening",
              bodyHTML: `<p class="muted">Loading summary…</p>`,
            })}
          </div>
          ${renderLostDealCard(d)}
          ${renderTimelinePanel(d)}
          <!-- SGNL-72: Sentiment trajectory slot; hydrated by hydrateSentimentTrajectory().
               SGNL-203: promoted from #6 → #3 (above the fold). -->
          <div data-testid="opp-sentiment-slot" data-sentiment-generation="">
            ${renderSentimentLoading()}
          </div>
          <!-- SGNL-152 next-move slot; hydrated by hydrateNextMove().
               SGNL-203: promoted from #7 → #4 (action card, high signal). -->
          <div
            data-testid="opp-next-move-slot"
            data-next-move-generation=""
          >${renderNextMoveLoading()}</div>
          <!-- SGNL-144 (Epic A PR-2): live Similar deals panel.
               SGNL-203: demoted from #3 → #5 (research, not interpret/act). -->
          <div data-testid="opp-similar-deals-slot">
            ${renderSimilarOppsLoading()}
          </div>
          <!-- SGNL-146: live Recommended POCs card.
               SGNL-203: demoted from #4 → #6. -->
          <div data-testid="opp-recommended-pocs-slot">
            ${renderRecommendedPocsLoading()}
          </div>
          <!-- SGNL-27: Document Evidence slot; hydrated by hydrateDocumentEvidence().
               SGNL-203: demoted from #5 → #7. -->
          <div data-testid="opp-evidence-slot" data-evidence-generation="">
            ${renderDocumentEvidenceLoading()}
          </div>
        </div>
      </div>
    </div>`;
}

function renderError(host: HTMLElement, oppId: string, err: Error): void {
  host.innerHTML = `
    <div class="tab-content">
      <div class="tab-header">
        <h2>Could not load opp ${esc(oppId)}</h2>
        <a class="back-link muted" href="#" data-action="back-to-list">← Back</a>
      </div>
      <p class="empty-bucket">${esc(err.message)}</p>
    </div>`;
  bindBackLink(host);
}

// ---- SGNL-152: Suggested next move card ----------------------------------------
//
// LLM-recommended next outbound action. Calls GET /api/opportunities/{id}/next-action.
// States: loading → populated | empty-state | error+retry.
// Every call fires a fresh Sonnet call (no cache at v0); the Refresh button
// simply re-calls the same GET — no separate POST/force endpoint needed.

function renderNextMoveLoading(): string {
  // SGNL-223 — spinner glyph + duration hint so operators see at-a-glance
  // that the call is in flight (previous muted-only text was easy to miss).
  // ↻ (U+21BB) is a static glyph with broad system-font coverage; the
  // .next-move-spinner CSS rule applies the keyframe rotation so we
  // don't need to load an SVG. The animation is suppressed under
  // prefers-reduced-motion (WCAG 2.1 SC 2.3.3) — the duration-hint
  // text below carries the loading signal in that case.
  return renderCollapsibleRightCard({
    card: "next-move",
    testid: "opp-next-move",
    headerHTML: "Suggested next move",
    bodyHTML: `
      <p data-testid="opp-next-move-loading" style="display:flex;align-items:center;gap:.5rem;margin:0">
        <span class="next-move-spinner" aria-hidden="true">↻</span>
        <span>Analysing deal context…</span>
      </p>
      <p class="muted" style="margin:.4rem 0 0;font-size:.85em">Usually 8&ndash;15 seconds</p>
    `,
  });
}

const REFRESH_NEXT_MOVE_BTN = `<button class="action-link" data-action="refresh-next-move" data-testid="next-move-refresh-btn" type="button">↺ Refresh</button>`;

function renderNextMoveEmpty(): string {
  return renderCollapsibleRightCard({
    card: "next-move",
    testid: "opp-next-move-empty",
    headerHTML: "Suggested next move",
    summaryActionHTML: `<button class="action-link" data-action="refresh-next-move" data-testid="next-move-refresh-btn" type="button">↺ Check again</button>`,
    bodyHTML: `<p class="empty-bucket">No recommendation yet — this opp hasn't been classified. Run the classifier and check back.</p>`,
  });
}

function renderNextMoveError(msg: string): string {
  const statusCode = /returned (\d{3})/.exec(msg)?.[1];
  const display = statusCode ? `HTTP ${statusCode}` : "request failed";
  return renderCollapsibleRightCard({
    card: "next-move",
    testid: "opp-next-move-error",
    headerHTML: "Suggested next move",
    summaryActionHTML: `<button class="action-link" data-action="refresh-next-move" type="button">Retry</button>`,
    bodyHTML: `<p class="muted">Couldn't load the recommendation (${esc(display)}).</p>`,
  });
}

function actionChip(action: NextActionOut["action"]): string {
  const label = action === "email" ? "✉ Email" : action === "call" ? "📞 Call" : "⏳ Wait";
  const colour = action === "email" ? "indigo" : action === "call" ? "blue" : "coral";
  return `<span class="chip ${colour}" data-testid="next-move-action-chip">${label}</span>`;
}

function urgencyChip(urgency: NextActionOut["urgency"]): string {
  const label = urgency === "high" ? "High urgency" : urgency === "medium" ? "Medium urgency" : "Low urgency";
  // low urgency intentionally muted (no bold colour) — chip with reduced opacity
  if (urgency === "low") return `<span class="chip" style="opacity:0.6" data-testid="next-move-urgency-chip">${label}</span>`;
  const colour = urgency === "high" ? "coral" : "indigo";
  return `<span class="chip ${colour}" data-testid="next-move-urgency-chip">${label}</span>`;
}

// SGNL-194 — header label shown on the editable address rows.
const _ACTION_LABEL: Record<NextActionOut["action"], string> = {
  email: "Email",
  call: "Call agenda",
  wait: "Internal note",
};

// ARCH-05 (PR #112 architect): when kind='thread', the `detail` field
// SHOULD carry the source_type prefix (e.g. "email:00T..." / "chatter:..."
// / "task:...") so a future deep-link click handler can parse the chip
// into a navigation target without expanding the schema. The taxonomy
// stays a closed enum; the prefix convention encodes the sub-source.
function _refKindLabel(kind: NextActionReference["kind"]): string {
  switch (kind) {
    case "signal":
      return "Signal";
    case "tag":
      return "Tag";
    case "thread":
      return "Thread";
    case "stage":
      return "Stage";
    case "fact":
    default:
      return "Fact";
  }
}

function renderReferencesBlock(refs: NextActionReference[] | undefined): string {
  if (!refs || refs.length === 0) return "";
  const chips = refs
    .map((r) => {
      const detail = r.detail ? ` title="${esc(r.detail)}"` : "";
      return `<li class="next-move-ref-chip" data-kind="${esc(r.kind)}"${detail}>
        <span class="next-move-ref-kind">${esc(_refKindLabel(r.kind))}</span>
        <span class="next-move-ref-label">${esc(r.label)}</span>
      </li>`;
    })
    .join("");
  return `
    <div class="next-move-refs" data-testid="next-move-refs">
      <p class="next-move-refs-title">References</p>
      <ul class="next-move-ref-list">${chips}</ul>
    </div>`;
}

function renderAddressRow(
  label: string,
  value: string | null | undefined,
  testid: string,
  editable: boolean,
): string {
  // contenteditable empties shown via `:empty::before` placeholder. Static
  // labels (e.g. "Subject") still render even when value is empty so the
  // AE has a visible row to type into.
  //
  // SEC-07 (PR #112 security): the value is escaped via `esc()` even
  // though it lands inside a contenteditable element's text content, as
  // defence-in-depth. The backend `_coerce_optional_text` strips HTML-
  // ish runs upstream; `esc()` here ensures no markup can reach the DOM
  // even if a future BE refactor drops the strip. Both layers are
  // intentional — do not remove either when refactoring.
  const escVal = value ? esc(value) : "";
  const editableAttr = editable ? 'contenteditable="true" spellcheck="true"' : "";
  return `
    <div class="next-move-addr-row">
      <span class="next-move-addr-label">${esc(label)}</span>
      <span
        class="next-move-addr-value"
        data-testid="${esc(testid)}"
        ${editableAttr}
      >${escVal}</span>
    </div>`;
}

function renderNextMoveCard(rec: NextActionOut): string {
  const hasDraft = Boolean(rec.draft?.trim());
  // Render markdown server-side-safe via the local safe renderer.
  // ESLint-friendly: imported renderSafeMarkdown takes plain text and
  // produces escaped HTML using a fixed allowlist of tags.
  const draftHtml = hasDraft ? renderSafeMarkdown(rec.draft) : "";
  // To: + Subject: rows only render for action types that have a recipient.
  const headerHtml =
    rec.action === "wait"
      ? ""
      : `
        <div class="next-move-address" data-testid="next-move-address">
          ${renderAddressRow("To", rec.recipient_hint ?? "", "next-move-to", true)}
          ${
            rec.action === "email"
              ? renderAddressRow("Subject", rec.subject_hint ?? "", "next-move-subject", true)
              : ""
          }
        </div>`;
  // Draft block is contenteditable so the AE can tweak before copy.
  // ARCH-01 (PR #112): store the original markdown source in a data
  // attribute so a future save-draft path has a clean source-of-truth
  // string (rather than serialising the mutated DOM). The attribute is
  // never mutated post-render; AE edits affect innerHTML only.
  // SEC-06 (PR #112): rich-text Copy writes text/html to the OS
  // clipboard. The tooltip surfaces this so AEs understand what's
  // being copied (customer name, deal context, formatting).
  const draftBlock = hasDraft
    ? `
      <div class="next-move-draft-block">
        <div class="next-move-draft-head">
          <p class="next-move-draft-label">${esc(_ACTION_LABEL[rec.action])}</p>
          <button
            class="action-link next-move-copy-btn"
            data-action="copy-next-move-draft"
            data-testid="next-move-copy-btn"
            type="button"
            title="Copy draft including customer details + formatting (rich text)"
            aria-label="Copy draft (rich text)"
          >Copy</button>
        </div>
        <div
          class="next-move-draft prose"
          data-testid="next-move-draft"
          data-draft-src="${esc(rec.draft)}"
          contenteditable="true"
          spellcheck="true"
        >${draftHtml}</div>
      </div>`
    : "";
  return renderCollapsibleRightCard({
    card: "next-move",
    testid: "opp-next-move",
    extraClass: "next-move-card",
    headerHTML: "Suggested next move",
    summaryActionHTML: REFRESH_NEXT_MOVE_BTN,
    bodyHTML: `
      <div class="next-move-chips">
        ${actionChip(rec.action)}
        ${urgencyChip(rec.urgency)}
        <span class="next-move-ai-badge" data-testid="next-move-ai-badge">✦ AI-generated</span>
      </div>
      <p class="next-move-reasoning">${esc(rec.reasoning)}</p>
      ${headerHtml}
      ${draftBlock}
      ${renderReferencesBlock(rec.references)}`,
  });
}

async function hydrateNextMove(
  host: HTMLElement,
  oppId: string,
  generation: string,
): Promise<void> {
  const slot = host.querySelector<HTMLElement>('[data-testid="opp-next-move-slot"]');
  if (!slot) return;
  try {
    // No client cache — every call triggers fresh Sonnet inference on the BE by design at v0.
    const rec = await fetchNextAction(oppId);
    const slotNow = host.querySelector<HTMLElement>('[data-testid="opp-next-move-slot"]');
    if (!slotNow || slotNow.dataset.nextMoveGeneration !== generation) return;
    slotNow.innerHTML = rec ? renderNextMoveCard(rec) : renderNextMoveEmpty();
    // Set textarea value via DOM property to avoid HTML-entity double-encoding.
    // esc(rec.draft) inside innerHTML would encode `&` → `&amp;` and the textarea
    // would display the entity literal instead of the original character.
    // SEC-M1 on PR #84.
    // SGNL-194: draft is now a contenteditable div rendered from
    // markdown by renderNextMoveCard — no separate DOM-property
    // assignment needed (the HTML was injected via innerHTML above).
  } catch (err) {
    const slotNow = host.querySelector<HTMLElement>('[data-testid="opp-next-move-slot"]');
    if (!slotNow || slotNow.dataset.nextMoveGeneration !== generation) return;
    slotNow.innerHTML = renderNextMoveError(err instanceof Error ? err.message : String(err));
  }
}

const _nextMoveAbort = new WeakMap<HTMLElement, AbortController>();
// Q-04 (PR #112 code review): per-host counter of in-flight refresh
// fetches. The refresh button is only re-enabled when the counter
// returns to zero — otherwise a rapid double-click can re-enable the
// button while a second fetch is still in flight (the first fetch's
// `finally` runs before the second completes).
const _nextMoveInFlight = new WeakMap<HTMLElement, number>();

function bindRefreshNextMove(host: HTMLElement, oppId: string): void {
  _nextMoveAbort.get(host)?.abort();
  const controller = new AbortController();
  _nextMoveAbort.set(host, controller);

  // SEC-03 / Q-03 (PR #112 review): intercept paste events into the
  // contenteditable draft div and insert plain text only. This keeps
  // `innerHTML` trustworthy for the Copy handler (no browser-generated
  // markup, no clipboard-relayed HTML from arbitrary sources). Bound
  // once per host via the same delegated-listener AbortController so
  // re-binds on hash navigation don't stack.
  host.addEventListener(
    "paste",
    (ev: ClipboardEvent) => {
      const target = ev.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.closest('[data-testid="next-move-draft"]')) return;
      const plain = ev.clipboardData?.getData("text/plain") ?? "";
      ev.preventDefault();
      if (plain) document.execCommand("insertText", false, plain);
    },
    { signal: controller.signal },
  );

  host.addEventListener(
    "click",
    (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      // Refresh button
      const refreshBtn = target.closest<HTMLButtonElement>('[data-action="refresh-next-move"]');
      if (refreshBtn) {
        e.preventDefault();
        const slot = host.querySelector<HTMLElement>('[data-testid="opp-next-move-slot"]');
        if (!slot) return;
        // Use crypto.randomUUID() for collision-free generation tokens;
        // Date.now() alone can collide under rapid repeated clicks. PR-Agent
        // inline suggestion importance 8 on PR #84.
        const newGen =
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random()}`;
        slot.dataset.nextMoveGeneration = newGen;
        slot.innerHTML = renderNextMoveLoading();
        // Disable the button while the ~3-4s Sonnet call is in flight.
        // Mirrors bindRefreshRecommendedPocs. ARCH-M1 on PR #84.
        refreshBtn.disabled = true;
        _nextMoveInFlight.set(host, (_nextMoveInFlight.get(host) ?? 0) + 1);
        void (async () => {
          try {
            await hydrateNextMove(host, oppId, newGen);
          } finally {
            const remaining = (_nextMoveInFlight.get(host) ?? 1) - 1;
            _nextMoveInFlight.set(host, remaining);
            if (remaining <= 0) {
              const btn = host.querySelector<HTMLButtonElement>(
                '[data-action="refresh-next-move"]',
              );
              if (btn) btn.disabled = false;
            }
          }
        })();
        return;
      }
      // Copy draft button
      const copyBtn = target.closest<HTMLButtonElement>('[data-action="copy-next-move-draft"]');
      if (copyBtn) {
        e.preventDefault();
        // SGNL-194 — draft is now a contenteditable rich-text div, not a
        // textarea. Source HTML + plain-text from it; write both
        // representations to the clipboard so paste into Gmail / Outlook /
        // Slack preserves bold + italic + lists, with a plain-text
        // fallback for clients that strip HTML.
        const draftEl = host.querySelector<HTMLElement>('[data-testid="next-move-draft"]');
        if (!draftEl) return;
        const html = draftEl.innerHTML.trim();
        const text = (draftEl.textContent ?? "").trim();
        if (!html && !text) return;

        const flash = (label: string, ms = 1800) => {
          const orig = copyBtn.textContent ?? "Copy";
          copyBtn.textContent = label;
          copyBtn.disabled = true;
          setTimeout(() => {
            copyBtn.textContent = orig;
            copyBtn.disabled = false;
          }, ms);
        };

        // execCommand fallback: select the live editable region and
        // invoke copy — preserves HTML in the OS clipboard on browsers
        // without the async clipboard API.
        //
        // PR-Agent (importance 6) on PR #112: focus the element first
        // (some browsers refuse execCommand on unfocused contenteditable);
        // capture the AE's current selection before clobbering it and
        // restore it afterwards so the fallback doesn't disrupt mid-edit
        // selections; guard against execCommand being absent on modern
        // browsers that have removed it.
        const execFallback = () => {
          try {
            draftEl.focus();
            const sel = window.getSelection();
            const prev =
              sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
            const range = document.createRange();
            range.selectNodeContents(draftEl);
            sel?.removeAllRanges();
            sel?.addRange(range);
            const ok =
              typeof document.execCommand === "function" && document.execCommand("copy");
            if (ok) flash("Copied!");
            else flash("Copy failed", 1200);
            sel?.removeAllRanges();
            if (prev) sel?.addRange(prev);
          } catch {
            // Silent — clipboard not available in this context.
          }
        };

        // Prefer ClipboardItem (writes both HTML + plain) so rich-text
        // paste lands in mail clients. Fall back to writeText (plain
        // only) and finally to execCommand.
        const w = window as unknown as {
          ClipboardItem?: typeof ClipboardItem;
        };
        const writeRich = async () => {
          if (
            navigator.clipboard &&
            typeof w.ClipboardItem === "function" &&
            typeof navigator.clipboard.write === "function"
          ) {
            const item = new w.ClipboardItem({
              "text/html": new Blob([html], { type: "text/html" }),
              "text/plain": new Blob([text], { type: "text/plain" }),
            });
            await navigator.clipboard.write([item]);
            flash("Copied!");
            return;
          }
          if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            flash("Copied!");
            return;
          }
          execFallback();
        };
        void writeRich().catch(execFallback);
      }
    },
    { signal: controller.signal },
  );
}

// PR-Agent #2 (importance 5) on PR #70 + architect ARCH-L2: stash an
// AbortController per host so re-running renderOppDetailPage (refetch,
// hash navigation back to same opp) doesn't stack delegated click
// listeners. WeakMap keyed by host so a GC'd host doesn't keep the
// controller alive.
const _citeClickAbort = new WeakMap<HTMLElement, AbortController>();

/** SGNL-128 — clicking a citation link inside the inline tag-chip
 * expansion scrolls the timeline to the cited row + briefly highlights
 * it. The `<a href="#opp/{id}?cite=...">` provides a graceful fallback
 * if JS doesn't run (the hash router re-mounts the page and the
 * on-mount `parseOppCite` path takes over); for the JS-running case we
 * just scroll-in-place. */
function bindCitationClicks(host: HTMLElement, oppId: string): void {
  // Abort the prior listener (if any) before attaching a fresh one.
  _citeClickAbort.get(host)?.abort();
  const controller = new AbortController();
  _citeClickAbort.set(host, controller);
  // Delegated handler — survives DOM re-renders inside the host.
  host.addEventListener("click", (e) => {
    const target = (e.target as HTMLElement | null)?.closest<HTMLAnchorElement>(
      "a.cite-link",
    );
    if (!target) return;
    const sourceType = target.dataset.citeSourceType;
    const sourceId = target.dataset.citeSourceId;
    if (!sourceType || !sourceId) return;
    // Same-opp shortcut: scroll-in-place rather than letting the
    // hash router re-mount the page.
    const currentOppId = parseOppHash(window.location.hash);
    if (currentOppId !== oppId) {
      return; // let the default `<a href>` take over
    }
    // code-reviewer F-01 on PR #70: preventDefault unconditionally
    // inside the same-opp branch. If scrollToCitation returns false
    // (row not in DOM — source row deleted post-classification), the
    // default <a href> click would otherwise change the hash, the
    // router would re-mount the page, and the on-mount cite path
    // would also no-op silently — net result: visible flicker for no
    // gain. Better to consume the click and leave the page state alone.
    e.preventDefault();
    scrollToCitation(host, { source_type: sourceType, source_id: sourceId });
  }, { signal: controller.signal });
}


// SGNL-212: stop summary-action button clicks (Refresh / Retry) from
// also toggling the <details> they live inside. The wrapper span
// carries data-stop-summary-toggle so the delegated handler is
// scoped to the affordance and doesn't swallow other summary clicks.
const _summaryStopAbort = new WeakMap<HTMLElement, AbortController>();

function bindCardSummaryActions(host: HTMLElement): void {
  _summaryStopAbort.get(host)?.abort();
  const controller = new AbortController();
  _summaryStopAbort.set(host, controller);
  host.addEventListener(
    "click",
    (e) => {
      const wrapper = (e.target as HTMLElement | null)?.closest(
        "[data-stop-summary-toggle]",
      );
      if (!wrapper) return;
      // The button inside fires its own click (Refresh/Retry has its
      // own data-action handler — bound elsewhere). We need to keep
      // that handler running but cancel the <summary>'s default
      // action, which is to toggle the parent <details>.
      //
      // PR-Agent #128 (importance 7): stopPropagation() on a host-
      // level delegate runs AFTER the event has bubbled through
      // <summary>, so it can't stop the bubble. preventDefault() IS
      // still effective here because the toggle is a 'default action'
      // dispatched after the click handler chain completes — calling
      // preventDefault on any handler in the chain (including a host-
      // level delegate) cancels it. Use both: stopPropagation as
      // defence-in-depth, preventDefault as the actual mechanism.
      e.preventDefault();
      e.stopPropagation();
    },
    { signal: controller.signal },
  );
}

// SGNL-212: "Show N more" toggle for the Similar Deals card.
// Uses a delegated listener on the host so the binding survives the
// async slot replacement done by hydrateSimilarOpps (the overflow div
// + the button only exist after the fetch resolves).
const _similarShowMoreAbort = new WeakMap<HTMLElement, AbortController>();

function bindSimilarDealsShowMore(host: HTMLElement): void {
  _similarShowMoreAbort.get(host)?.abort();
  const controller = new AbortController();
  _similarShowMoreAbort.set(host, controller);
  host.addEventListener(
    "click",
    (e) => {
      const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(
        '[data-action="similar-deals-show-more"]',
      );
      if (!btn) return;
      e.preventDefault();
      // Find the sibling overflow div within the same Similar Deals card.
      const card = btn.closest('[data-testid="opp-similar-deals"]');
      const overflow = card?.querySelector<HTMLElement>(
        '[data-testid="opp-similar-deals-overflow"]',
      );
      if (!overflow) return;
      // PR-review a11y / CR fix: TOGGLE — keep the button in the DOM,
      // flip `aria-expanded`, swap the label between "Show N more" and
      // "Show fewer". Reveal-once was destroying focus and removing the
      // operator's ability to re-collapse.
      const isExpanded = btn.getAttribute("aria-expanded") === "true";
      overflow.hidden = isExpanded;
      btn.setAttribute("aria-expanded", String(!isExpanded));
      const moreLabel = btn.dataset.labelMore ?? "Show more";
      const fewerLabel = btn.dataset.labelFewer ?? "Show fewer";
      btn.textContent = isExpanded ? moreLabel : fewerLabel;
    },
    { signal: controller.signal },
  );
}

function bindBackLink(host: HTMLElement): void {
  for (const a of host.querySelectorAll<HTMLAnchorElement>(
    'a[data-action="back-to-list"]',
  )) {
    a.addEventListener("click", (e) => {
      e.preventDefault();
      // Clear hash → main.ts state-driven render reverts to the active
      // tab the user was on before they drilled into this opp.
      window.location.hash = "";
    });
  }
}

function bindRefetch(host: HTMLElement, oppId: string): void {
  const btn = host.querySelector<HTMLButtonElement>(
    '[data-testid="opp-refetch-btn"]',
  );
  const feedback = host.querySelector<HTMLElement>(
    '[data-testid="opp-refetch-feedback"]',
  );
  if (!btn || !feedback) return;
  btn.addEventListener("click", async () => {
    btn.disabled = true;
    feedback.textContent = "⏳ pulling and reclassifying…";
    try {
      const r = await fetch(
        `/api/admin/opps/${encodeURIComponent(oppId)}/pull-and-reclassify`,
        { method: "POST", credentials: "same-origin" },
      );
      if (!r.ok) {
        const raw = await r.text();
        throw new Error(`HTTP ${r.status}: ${raw}`);
      }
      feedback.textContent =
        "✓ Done — re-rendering page to show fresh conversation entries.";
      // Cheapest correct refresh: re-fetch /detail and re-render in place.
      await renderOppDetailPage(host, oppId);
    } catch (err) {
      btn.disabled = false;
      feedback.textContent = `✗ ${(err as Error).message}`;
    }
  });
}

export async function renderOppDetailPage(
  host: HTMLElement,
  oppId: string,
): Promise<void> {
  host.innerHTML = `<div class="loading">Loading opportunity ${esc(oppId)}…</div>`;
  try {
    const detail = await fetchOpportunityDetail(oppId);
    host.innerHTML = renderShell(detail);
    bindBackLink(host);
    bindRefetch(host, detail.id);
    bindCitationClicks(host, detail.id);
    // SGNL-101 — fetch the rollup AFTER the detail loads, in parallel
    // with the user reading the page. The rollup may trigger an LLM
    // call (a few seconds) the first time it's stale, so we don't
    // block initial render on it.
    //
    // CR Q5 on PR #58 — stamp the slot with a generation id so a
    // late-arriving rollup fetch from a PREVIOUS render (e.g. operator
    // hops from opp A → opp B before A's rollup resolves) doesn't
    // overwrite the current page. hydrateRollup re-reads the id and
    // bails if it changed.
    const slot = host.querySelector<HTMLElement>(
      '[data-testid="opp-conversation-rollup-slot"]',
    );
    const generation = String(Date.now());
    if (slot) slot.dataset.rollupGeneration = generation;
    void hydrateRollup(host, detail.id, generation);
    // SGNL-144 (Epic A PR-2): kick the Similar deals fetch in
    // parallel with the rollup. Same generation stamp so a navigation
    // mid-fetch can't overwrite the next page.
    const simSlot = host.querySelector<HTMLElement>(
      '[data-testid="opp-similar-deals-slot"]',
    );
    if (simSlot) simSlot.dataset.similarGeneration = generation;
    void hydrateSimilarOpps(host, detail.id, generation);
    bindSimilarDealsShowMore(host);
    // SGNL-212: stop Refresh/Retry buttons inside <summary> from
    // toggling the <details>. Bound on the host so it survives the
    // async slot replacements done by hydrateXxx.
    bindCardSummaryActions(host);
    // SGNL-146 — Recommended POCs card. Same generation-stamp pattern
    // as Similar deals. Lazy backend compute on cache miss.
    const pocSlot = host.querySelector<HTMLElement>(
      '[data-testid="opp-recommended-pocs-slot"]',
    );
    if (pocSlot) pocSlot.dataset.pocGeneration = generation;
    void hydrateRecommendedPocs(host, detail.id, generation);
    bindRefreshRecommendedPocs(host, detail.id);
    // SGNL-152 — Suggested next move card. Kicks off in parallel with
    // the other hydrations; same generation stamp for race safety.
    const nextMoveSlot = host.querySelector<HTMLElement>(
      '[data-testid="opp-next-move-slot"]',
    );
    if (nextMoveSlot) nextMoveSlot.dataset.nextMoveGeneration = generation;
    void hydrateNextMove(host, detail.id, generation);
    bindRefreshNextMove(host, detail.id);
    // SGNL-27 — Document Evidence. Kicked off in parallel; generation
    // stamp guards against stale resolves after navigation.
    const evidenceSlot = host.querySelector<HTMLElement>('[data-testid="opp-evidence-slot"]');
    if (evidenceSlot) evidenceSlot.dataset.evidenceGeneration = generation;
    void hydrateDocumentEvidence(host, detail.id, generation);
    // SGNL-72 — Sentiment trajectory. Kicks off in parallel; BE scores
    // unscored messages lazily on first hit (may take a few seconds).
    const sentimentSlot = host.querySelector<HTMLElement>('[data-testid="opp-sentiment-slot"]');
    if (sentimentSlot) sentimentSlot.dataset.sentimentGeneration = generation;
    void hydrateSentimentTrajectory(host, detail.id, generation);
    bindRefreshSentiment(host, detail.id);
    // SGNL-220 — Lost-deal LLM coaching. Slot is only present when
    // the opp is Closed Lost (renderLostDealCard returns "" otherwise);
    // the hydrate function bails silently when the slot is missing.
    const coachingSlot = host.querySelector<HTMLElement>(
      '[data-testid="opp-lost-coaching-slot"]',
    );
    if (coachingSlot) {
      coachingSlot.dataset.coachingGeneration = generation;
      void hydrateLostCoaching(host, detail.id, generation);
      bindRefreshLostCoaching(host, detail.id);
    }
    // SGNL-221 — Lost-deal pattern matches. Sibling slot to the
    // coaching one; hydrates in parallel so neither blocks the other.
    // Reuses the same generation stamp so an opp-switch invalidates
    // both in-flight responses atomically.
    const patternSlot = host.querySelector<HTMLElement>(
      '[data-testid="opp-lost-pattern-slot"]',
    );
    if (patternSlot) {
      patternSlot.dataset.patternGeneration = generation;
      void hydrateLostPattern(host, detail.id, generation);
    }
    // SGNL-149: bind the Draft Message click delegate. Single
    // AbortController per host so re-mounts don't stack listeners.
    bindDraftMessageButtons(host, detail.id);
    // SGNL-128 — if the deep-link carries ?cite=source_type:source_id,
    // scroll the timeline to the cited row + briefly highlight it.
    // Schedule on the microtask queue so the timeline DOM is mounted
    // by the time we query for the row (renderShell is synchronous;
    // hydrateRollup is async but doesn't touch the timeline).
    const cite = parseOppCite(window.location.hash);
    if (cite) {
      window.requestAnimationFrame(() => {
        scrollToCitation(host, cite);
      });
    }
  } catch (err) {
    renderError(host, oppId, err as Error);
  }
}

// SGNL-144 (Epic A PR-2): fetch + render the Similar deals panel.
// Same generation-stamp pattern as hydrateRollup — if the operator
// navigates away mid-fetch, a stale resolve doesn't overwrite the
// next page.
async function hydrateSimilarOpps(
  host: HTMLElement,
  oppId: string,
  generation: string,
): Promise<void> {
  const slot = host.querySelector<HTMLElement>(
    '[data-testid="opp-similar-deals-slot"]',
  );
  if (!slot) return;
  try {
    const resp = await fetchSimilarOpps(oppId, { n: 5, sortBy: "won_value" });
    const slotNow = host.querySelector<HTMLElement>(
      '[data-testid="opp-similar-deals-slot"]',
    );
    if (!slotNow || slotNow.dataset.similarGeneration !== generation) return;
    slotNow.innerHTML = renderSimilarOppsCard(resp);
  } catch (err) {
    const slotNow = host.querySelector<HTMLElement>(
      '[data-testid="opp-similar-deals-slot"]',
    );
    if (!slotNow || slotNow.dataset.similarGeneration !== generation) return;
    const msg = err instanceof Error ? err.message : String(err);
    slotNow.innerHTML = renderSimilarOppsError(msg);
  }
}

// SGNL-146 — Recommended POCs card hydration. Mirrors Similar deals'
// generation-stamp pattern so a stale fetch can't overwrite the next
// page after navigation.
async function hydrateRecommendedPocs(
  host: HTMLElement,
  oppId: string,
  generation: string,
): Promise<void> {
  const slot = host.querySelector<HTMLElement>(
    '[data-testid="opp-recommended-pocs-slot"]',
  );
  if (!slot) return;
  try {
    const resp = await fetchRecommendedPocs(oppId);
    const slotNow = host.querySelector<HTMLElement>(
      '[data-testid="opp-recommended-pocs-slot"]',
    );
    if (!slotNow || slotNow.dataset.pocGeneration !== generation) return;
    slotNow.innerHTML = renderRecommendedPocsCard(resp);
  } catch (err) {
    const slotNow = host.querySelector<HTMLElement>(
      '[data-testid="opp-recommended-pocs-slot"]',
    );
    if (!slotNow || slotNow.dataset.pocGeneration !== generation) return;
    const msg = err instanceof Error ? err.message : String(err);
    slotNow.innerHTML = renderRecommendedPocsError(msg);
  }
}

// Click delegation for the "Refresh" button inside the Recommended
// POCs card. Single AbortController per host so re-mounting the page
// (e.g. hash navigation back to the same opp) doesn't stack listeners.
const _pocRefreshAbort = new WeakMap<HTMLElement, AbortController>();

function bindRefreshRecommendedPocs(host: HTMLElement, oppId: string): void {
  _pocRefreshAbort.get(host)?.abort();
  const controller = new AbortController();
  _pocRefreshAbort.set(host, controller);
  host.addEventListener(
    "click",
    (e) => {
      const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(
        '[data-action="refresh-recommended-pocs"]',
      );
      if (!btn) return;
      e.preventDefault();
      const slot = host.querySelector<HTMLElement>(
        '[data-testid="opp-recommended-pocs-slot"]',
      );
      if (!slot) return;
      // Bump the generation so the in-flight initial fetch (if any)
      // can't race-overwrite the refresh result.
      const newGen = `${Date.now()}-refresh`;
      slot.dataset.pocGeneration = newGen;
      slot.innerHTML = renderRecommendedPocsLoading();
      btn.disabled = true;
      void (async () => {
        try {
          const resp = await fetchRecommendedPocsRefresh(oppId);
          const slotNow = host.querySelector<HTMLElement>(
            '[data-testid="opp-recommended-pocs-slot"]',
          );
          if (!slotNow || slotNow.dataset.pocGeneration !== newGen) return;
          slotNow.innerHTML = renderRecommendedPocsCard(resp);
        } catch (err) {
          const slotNow = host.querySelector<HTMLElement>(
            '[data-testid="opp-recommended-pocs-slot"]',
          );
          if (!slotNow || slotNow.dataset.pocGeneration !== newGen) return;
          const msg = err instanceof Error ? err.message : String(err);
          slotNow.innerHTML = renderRecommendedPocsError(msg);
        }
      })();
    },
    { signal: controller.signal },
  );
}

// ---- SGNL-149: Draft Message modal -----------------------------------------

const _draftBtnAbort = new WeakMap<HTMLElement, AbortController>();

function renderDraftModal(pocName: string, body: string): string {
  // Body is the inner content area (loading / error / populated).
  // The shell + close-button + Esc-key handling stays consistent
  // across states so the modal is dismissable in any state.
  return `
    <div class="poc-draft-modal-backdrop" data-testid="poc-draft-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="poc-draft-modal-title">
      <div class="draft-modal">
        <div style="display:flex; justify-content:space-between; align-items:baseline; margin-bottom:12px;">
          <h2 id="poc-draft-modal-title" style="margin:0; font-size:1.1em;">Draft message · ${esc(pocName)}</h2>
          <button class="action-link" data-action="close-draft-modal" type="button" aria-label="Close">Close</button>
        </div>
        ${body}
      </div>
    </div>`;
}

function renderDraftModalLoading(pocName: string): string {
  return renderDraftModal(
    pocName,
    `<p class="muted" data-testid="poc-draft-modal-loading">Drafting a tailored pitch…</p>`,
  );
}

function renderDraftModalError(pocName: string, msg: string): string {
  return renderDraftModal(
    pocName,
    `
      <p class="muted">Couldn't draft the message (${esc(msg)}).</p>
      <p><button class="action-link" data-action="retry-draft-modal" type="button">Retry</button></p>
    `,
  );
}

function renderDraftModalEmpty(pocName: string): string {
  return renderDraftModal(
    pocName,
    `
      <p class="empty-bucket">This opp doesn't have a classifier summary yet — there's nothing for Sonnet to ground the draft against. Wait for the next 06:00 UTC classifier tick (or trigger a manual reclassify) and retry.</p>
    `,
  );
}

function renderDraftModalPopulated(pocName: string, draft: string): string {
  return renderDraftModal(
    pocName,
    `
      <p class="muted" style="margin:0 0 8px;">Edit as needed, then copy and paste into your channel. The AE adds the greeting + signature.</p>
      <textarea data-testid="poc-draft-modal-textarea" rows="12" style="width:100%; padding:8px; border:1px solid var(--line); border-radius:4px; font-family:inherit; font-size:0.95em; resize:vertical;">${esc(draft)}</textarea>
      <div style="margin-top:12px; display:flex; gap:8px; align-items:center;">
        <button class="action-link" data-action="copy-draft-to-clipboard" type="button" data-testid="poc-draft-copy">Copy to clipboard</button>
        <span class="muted" data-testid="poc-draft-copy-feedback" style="font-size:0.9em;"></span>
      </div>
    `,
  );
}

function mountDraftModal(host: HTMLElement, html: string): HTMLElement {
  // Single in-flight modal per host. If one is already open, REMOVE
  // it before mounting the replacement — pre-fix `outerHTML = html`
  // left the captured `modal` variable pointing at a detached node,
  // and a rapid second click could query/replace the wrong node mid-
  // flight (code-review HIGH-1).
  host.querySelector("[data-testid='poc-draft-modal-backdrop']")?.remove();
  const container = document.createElement("div");
  container.innerHTML = html;
  const node = container.firstElementChild as HTMLElement;
  host.appendChild(node);
  return node;
}

function closeDraftModal(host: HTMLElement): void {
  const modal = host.querySelector<HTMLElement>("[data-testid='poc-draft-modal-backdrop']");
  modal?.remove();
}

function bindDraftMessageButtons(host: HTMLElement, oppId: string): void {
  _draftBtnAbort.get(host)?.abort();
  const controller = new AbortController();
  _draftBtnAbort.set(host, controller);

  // Monotonically-increasing request token. Each runDraft() invocation
  // claims a fresh token; any in-flight request whose token is no
  // longer the latest is silently dropped (PR-Agent #3233714488 +
  // code-review LOW-6). Without this, two rapid Draft Message clicks
  // would race — earlier resolve could overwrite the later modal
  // with stale content for a different POC.
  let currentToken = 0;

  async function runDraft(pocId: string, pocName: string): Promise<void> {
    const token = ++currentToken;
    mountDraftModal(host, renderDraftModalLoading(pocName));
    // Stash (pocId, pocName) on the modal node via data attributes so
    // the Retry button reads from DOM, not from a closure-captured
    // variable that a concurrent click could mutate.
    const modal = host.querySelector<HTMLElement>("[data-testid='poc-draft-modal-backdrop']");
    if (modal) {
      modal.dataset.draftPocId = pocId;
      modal.dataset.draftPocName = pocName;
    }
    try {
      const resp = await fetchPocDraftMessage(oppId, pocId);
      if (token !== currentToken) return;  // stale — a newer click owns the UI
      if (!resp) {
        mountDraftModal(host, renderDraftModalEmpty(pocName));
      } else {
        mountDraftModal(host, renderDraftModalPopulated(pocName, resp.draft));
      }
    } catch (err) {
      if (token !== currentToken) return;
      const msg = err instanceof Error ? err.message : String(err);
      mountDraftModal(host, renderDraftModalError(pocName, msg));
    }
    // Re-stamp the data attrs on the (possibly new) modal node so
    // Retry sees the same (pocId, pocName) the request used.
    const finalModal = host.querySelector<HTMLElement>(
      "[data-testid='poc-draft-modal-backdrop']",
    );
    if (finalModal) {
      finalModal.dataset.draftPocId = pocId;
      finalModal.dataset.draftPocName = pocName;
    }
  }

  host.addEventListener(
    "click",
    (e) => {
      // PR-Agent #3233714469: `e.target` can be a `Text` node, not an
      // HTMLElement; `closest()` is only defined on `Element`. Guard
      // with `instanceof Element` to avoid sporadic runtime errors
      // on click handlers fired on text-node targets (rare but
      // browser-dependent).
      const target = e.target;
      if (!(target instanceof Element)) return;

      const draftBtn = target.closest<HTMLButtonElement>(
        '[data-action="poc-draft-message"]',
      );
      if (draftBtn) {
        e.preventDefault();
        const pocId = draftBtn.dataset.pocId ?? "";
        const pocName = draftBtn.dataset.pocName ?? pocId;
        if (!pocId) return;
        void runDraft(pocId, pocName);
        return;
      }

      if (target.closest('[data-action="close-draft-modal"]')) {
        e.preventDefault();
        // Bump token so an in-flight request can't re-mount the modal
        // after the user has explicitly closed it.
        currentToken++;
        closeDraftModal(host);
        return;
      }

      if (target.closest('[data-action="retry-draft-modal"]')) {
        e.preventDefault();
        // Retry reads (pocId, pocName) from the modal's data attrs —
        // not from a closure variable that a concurrent click could
        // have mutated (code-review LOW-6).
        const modal = host.querySelector<HTMLElement>(
          "[data-testid='poc-draft-modal-backdrop']",
        );
        const pocId = modal?.dataset.draftPocId ?? "";
        const pocName = modal?.dataset.draftPocName ?? pocId;
        if (pocId) {
          void runDraft(pocId, pocName);
        }
        return;
      }

      if (target.closest('[data-action="copy-draft-to-clipboard"]')) {
        e.preventDefault();
        const textarea = host.querySelector<HTMLTextAreaElement>(
          '[data-testid="poc-draft-modal-textarea"]',
        );
        const feedback = host.querySelector<HTMLElement>(
          '[data-testid="poc-draft-copy-feedback"]',
        );
        if (!textarea) return;
        const text = textarea.value;
        // navigator.clipboard is the modern path; fallback to
        // execCommand for environments where the Clipboard API is
        // blocked (some sandbox / iframe scenarios). The textarea
        // is already mounted so execCommand("copy") is straightforward.
        const writeViaApi = navigator.clipboard?.writeText?.(text);
        const onOk = () => {
          if (feedback) feedback.textContent = "✓ Copied. Edit + send through your channel.";
        };
        const onErr = () => {
          // Fallback: select + execCommand
          textarea.select();
          try {
            document.execCommand("copy");
            onOk();
          } catch {
            if (feedback) feedback.textContent = "Couldn't copy automatically — select all + Ctrl/⌘-C.";
          }
        };
        if (writeViaApi && typeof writeViaApi.then === "function") {
          writeViaApi.then(onOk, onErr);
        } else {
          onErr();
        }
      }
    },
    { signal: controller.signal },
  );

  // Esc closes the modal — bound at host level so it survives the
  // hash-router re-mount cycle (same lifecycle as the click handler).
  host.addEventListener(
    "keydown",
    (e) => {
      if (e.key === "Escape") {
        closeDraftModal(host);
      }
    },
    { signal: controller.signal },
  );
}

async function hydrateRollup(
  host: HTMLElement,
  oppId: string,
  generation: string,
): Promise<void> {
  const slot = host.querySelector<HTMLElement>(
    '[data-testid="opp-conversation-rollup-slot"]',
  );
  if (!slot) return;
  try {
    const rollup = await fetchConversationRollup(oppId);
    // Re-look up the slot after the await — the DOM may have been
    // replaced by a navigation. If the generation stamp no longer
    // matches, a newer render owns the page; do nothing.
    const slotNow = host.querySelector<HTMLElement>(
      '[data-testid="opp-conversation-rollup-slot"]',
    );
    if (!slotNow || slotNow.dataset.rollupGeneration !== generation) return;
    slotNow.innerHTML = renderRollupSection(rollup);
  } catch (err) {
    const slotNow = host.querySelector<HTMLElement>(
      '[data-testid="opp-conversation-rollup-slot"]',
    );
    if (!slotNow || slotNow.dataset.rollupGeneration !== generation) return;
    const msg = err instanceof Error ? err.message : String(err);
    // SGNL-212 PR-review CR HIGH: the error branch escaped the
    // collapsible pattern (every other rollup state is collapsible).
    // Render through the helper so this state is consistent.
    slotNow.innerHTML = renderCollapsibleRightCard({
      card: "conversation-rollup",
      testid: "opp-conversation-rollup-error",
      headerHTML: "What's been happening",
      bodyHTML: `<p class="muted">Couldn't load summary (${esc(msg)}).</p>`,
    });
  }
}

// Exposed for hash-router parsing in main.ts.
export function parseOppHash(hash: string): string | null {
  // Accept "#opp/006xxx", "#/opp/006xxx", and an optional "?cite=…"
  // query suffix the SGNL-128 citation deep-link adds. The cite param
  // is intentionally ignored here — `parseOppCite` extracts it.
  const m = hash.match(/^#\/?opp\/([A-Za-z0-9]{15,18})(?:\?.*)?$/);
  return m ? m[1] : null;
}

/** SGNL-128 — parse the `?cite=source_type:source_id` query string off
 * a hash like `#opp/006xxx?cite=task:abc-123`. Returns `null` when no
 * cite param is present or the value is malformed. */
export function parseOppCite(
  hash: string,
): { source_type: string; source_id: string } | null {
  const q = hash.match(/[?&]cite=([^&]+)/);
  if (!q) return null;
  // PR-Agent #2 (security, importance 7) on PR #69:
  // decodeURIComponent throws URIError on malformed `%` sequences
  // (e.g. `?cite=%XX`). An unhandled throw on page mount would crash
  // the Opp Detail render. Guard via try/catch and treat a malformed
  // cite as "no cite param" — the page still loads, just without the
  // scroll-to-citation behaviour.
  let decoded: string;
  try {
    decoded = decodeURIComponent(q[1]);
  } catch {
    return null;
  }
  const colon = decoded.indexOf(":");
  if (colon <= 0 || colon === decoded.length - 1) return null;
  const source_type = decoded.slice(0, colon);
  const source_id = decoded.slice(colon + 1);
  // Whitelist the source_type so a malformed hash can't trick us into
  // a querySelector with arbitrary attribute values.
  if (
    source_type !== "task"
    && source_type !== "chatter"
    && source_type !== "event"
    && source_type !== "note"
    && source_type !== "email"
    && source_type !== "attachment"
  ) {
    return null;
  }
  // source_id format guard (PR-Agent #2 follow-up): valid source_ids
  // are SF/UUID-shaped — alnum, `-`, `_`, `:`, `.`. Reject anything
  // outside that set so even with CSS.escape downstream we don't
  // forward an obviously-malformed deep-link.
  if (!/^[A-Za-z0-9_\-:.]+$/.test(source_id)) {
    return null;
  }
  return { source_type, source_id };
}

/** SGNL-128 — find the timeline row matching the cited source row,
 * scroll it into view, and add `.cited-highlight` for ~2s. Returns
 * `true` if the row was found, `false` otherwise (operator clicked a
 * citation for an opp whose timeline doesn't include the cited row —
 * e.g., source was deleted post-classification). Exposed so the tag
 * popover's in-page click can reuse the same code path. */
export function scrollToCitation(
  host: HTMLElement,
  cite: { source_type: string; source_id: string },
): boolean {
  // security-auditor M (SEC-002) on PR #69: escape both source_type
  // and source_id before they land in an attribute selector.
  // source_type is whitelisted by parseOppCite, source_id matches
  // `[A-Za-z0-9_\-:.]+` — both already safe — but defense-in-depth
  // costs nothing. CSS.escape is the canonical browser API; jsdom
  // (test runtime) doesn't ship it, so we fall back to a minimal
  // hand-rolled escape that backslashes anything outside the safe
  // ASCII subset. Real browsers (Chrome/Edge per Bounteous SSO) all
  // expose CSS.escape, so production goes through the canonical path.
  const escAttr = (s: string): string =>
    typeof CSS !== "undefined" && typeof CSS.escape === "function"
      ? CSS.escape(s)
      : s.replace(/[^A-Za-z0-9_\-]/g, (ch) => "\\" + ch);
  const sel =
    `[data-source-type="${escAttr(cite.source_type)}"]`
    + `[data-source-id="${escAttr(cite.source_id)}"]`;
  const row = host.querySelector<HTMLElement>(sel);
  if (!row) return false;
  row.scrollIntoView({ behavior: "smooth", block: "center" });
  row.classList.add("cited-highlight");
  // Removed after the animation so a second click can re-trigger.
  window.setTimeout(() => row.classList.remove("cited-highlight"), 2000);
  return true;
}
